/**
 * ConversationPanel Component
 * Encapsulates all per-conversation logic: header, messages, input, streaming, model selection.
 * Extracted from ChatPage.tsx to support split-panel (branching) layouts.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Alert, Button, message as antdMessage } from 'antd';
import { MessageList } from '@modules/conversations/components/MessageList';
import type { MessageFeedback } from '@modules/conversations/components/MessageRating';
// Leaf import, not the projects barrel — see the note in Sidebar.tsx.
import { ProjectThreadStrip } from '@modules/projects/components/ProjectThreadStrip';
import { ModeSelector } from '@modules/conversations/components/ModeSelector';
import { ContextMeter } from '@modules/conversations/components/ContextMeter';
import { AgentConnectors } from '@modules/conversations/components/connectors/AgentConnectors';
import { ChatInput } from '@modules/conversations/components/ChatInput';
import { DriveFilePanel } from '@modules/conversations/components/drive/DriveFilePanel';
import { GoogleDriveGlyph } from '@modules/conversations/components/drive/driveIcons';
import { driveService } from '@modules/conversations/services/driveService';
import { findModelEntry } from '@modules/conversations/components/ModelSelector';
import { resolveAgentTextureColor } from '@/brand/textures';
import { readCachedAgentColor, writeCachedAgentColor } from '../utils/agentColorCache';
import type { SendOutcome } from '../utils/returnedDraft';
import type { ToolDebugEvent, ActiveSkillEntry } from '@modules/conversations/components/AgentDebugPanel';
import conversationService, { ApiError, compactionInfoOf } from '@modules/conversations/services/conversationService';
import { showUploadErrorModal, UploadError } from '@modules/conversations/services/fileValidationService';
import { botService } from '@modules/chatbots';
import { AgentDebugPanel } from '@modules/conversations/components/AgentDebugPanel';
import { AgentPickerModal } from '@modules/conversations/components/AgentPickerModal';
import type { HandoffOpenMode } from '@modules/conversations/components/AgentPickerModal';
import toolInvocationService from '@modules/observability/services/toolInvocationService';
import api from '@/services/api';
import { useLanguage } from '@/hooks/useLanguage';
import { useAuth } from '@/hooks/useAuth';
import { useFeature } from '@/hooks/useFeature';
import { useDriveConnected } from '../hooks/useDriveConnected';
import { isReservedSlug } from '@/routing';
import { useBrand } from '@/brand';
import { useArtifacts } from '../hooks/useArtifacts';
import ArtifactPanel from './artifacts/ArtifactPanel';
import WikiGraphPanel, { WikiWIcon } from '@modules/wiki/components/WikiGraphPanel';
import { useWikiGraph } from '@modules/wiki/hooks/useWikiGraph';
import {
  liveStatusEntriesEqual,
  resolveLiveStatusLabel,
  type LiveStatusEntry,
  type ThinkingPhaseLit,
} from '../utils/progressStatus';
import type { Conversation, Message, Bot, BranchResult, ThinkingStep, StreamEvent, DriveFileRef, DriveEdit, ContextUsage, BackendMessage } from '@/types/models';
import { cachedGet } from '@services/cachedGet';
import { showNotification } from '@/utils/showNotification';
import { reconcileStoppedTurn, settleStoppedTurn, STOP_RECONCILE_DELAY_MS } from '@/utils/stopReconcile';
import { stoppedMessageId, stoppedTurnOf, withStoredStop } from '@modules/conversations/utils/stoppedAnswer';
import { frozenAtStop } from '@/utils/stopFreeze';
import { answeredByOf, ranLabel, substitutionNames } from '@/utils/modelLabels';
import { modelCatalogUrl } from '@/utils/modelPicker';
import { providerOfModel } from '@/utils/modelProvider';
import { startingModel } from '@/utils/conversationModel';
import { createLogger } from '@/utils/logger';
import { TurnGoogleAccessCard, useTurnGoogleAccess } from '@/modules/cloud-storage/components/TurnGoogleAccess';
import { TurnConnectorAccessCard, useTurnConnectorAccess } from '@/modules/connector_library/components/TurnConnectorAccess';
import './ConversationPanel.css';

const logger = createLogger('ConversationPanel');
import '../pages/ChatPage.css';
import ConversationHeaderTitle from '@modules/conversations/components/ConversationHeaderTitle';
import { useSupportConversation } from '@/components/layout/supportConversation';

/**
 * One entry of the MODEL FAMILIES catalog returned by /ai/models/.
 * `id` is the stable family key ('sonnet', 'gpt-mini', …) users select and
 * that gets sent to the backend; `resolved_model`/`resolved_model_name` is
 * the admin-pinned concrete version — read-only display info.
 */
interface CatalogModel {
  id: string;
  name: string;
  provider?: string;
  description?: string;
  supports_vision?: boolean;
  supports_web_search?: boolean;
  resolved_model?: string;
  resolved_model_name?: string;
  /** Every release in this family — lets a legacy stored ref still match. */
  version_ids?: string[];
  /** Each release's own name (utils/modelLabels.refLabel). */
  version_names?: Record<string, string>;
  /** Switched-off family keys this family runs ("gpt" on GPT Terra). */
  aliases?: string[];
  enabled?: boolean;
}

/** Pick only the catalog fields the panel needs from a raw API entry. */
const toCatalogModel = (m: any): CatalogModel => ({
  id: m.id,
  name: m.name,
  provider: m.provider,
  description: m.description,
  supports_vision: m.supports_vision,
  supports_web_search: m.supports_web_search,
  resolved_model: m.resolved_model,
  resolved_model_name: m.resolved_model_name,
  version_ids: m.version_ids,
  version_names: m.version_names,
  aliases: m.aliases,
  enabled: m.enabled,
});

// Module-level cache to survive React key-based remounts.
// Used when external navigation (e.g. sidebar click) causes a remount
// right after a conversation was just created internally.
// Keyed by conversationId so multiple panels don't clobber each other.
const _panelCache = new Map<string, { conversation: Conversation; bot: Bot | null }>();
const MAX_PANEL_CACHE_SIZE = 5;


// Module-level bot cache, keyed by chatbot_id. Sibling branch panels share
// the same bot — without this cache, each branch panel re-fetches and the
// mode slider flickers/disappears between branch switches because its render
// gate (`bot?.mode_slider_config?.enabled`) is false until the async fetch
// resolves. Populated by every successful fetchBotShared call below;
// consumed by the lazy `bot` state initializer so newly-mounted branch
// panels render with the bot synchronously available.
const _botCache = new Map<string, Bot>();
// In-flight fetch promises keyed by chatbot_id. When several panels mount
// concurrently (Main + freshly-created Branch), they share one fetch instead
// of racing — eliminates the window where the second panel resolves to a
// missing bot and hides the slider.
const _botFetching = new Map<string, Promise<Bot>>();

async function fetchBotShared(botId: string, forceFresh = false): Promise<Bot> {
  // forceFresh=true bypasses the in-memory cache so callers that need the
  // latest bot config (e.g. starting a new conversation, where the bot's
  // mode_slider_config.default_index may have changed since the cache was
  // populated) get the freshest data. Otherwise the cached copy from before
  // the bot was last edited would win until a full page refresh.
  if (!forceFresh) {
    const cached = _botCache.get(botId);
    if (cached) return cached;
  }
  let inFlight = _botFetching.get(botId);
  if (!inFlight) {
    inFlight = botService.getBot(botId)
      .then((bot) => {
        _botCache.set(bot.chatbot_id, bot);
        _botFetching.delete(botId);
        return bot;
      })
      .catch((err) => {
        _botFetching.delete(botId);
        throw err;
      });
    _botFetching.set(botId, inFlight);
  }
  return inFlight;
}

export interface ConversationPanelProps {
  initialConversationId?: string;
  initialBotId?: string;
  initialMessages?: Message[];
  initialConversation?: Conversation;
  /** Pre-known agent color slug ('blue' | 'green' | 'red' | 'yellow' | hex).
   *  When the parent already knows which agent the user picked (e.g.
   *  MainPage → ConversationPanel handoff after the first send), pass it
   *  here so the panel's `data-agent-color` is correct on the FIRST paint.
   *  Without this, the panel mounts with a 'blue' default while the bot
   *  loads, causing a visible color flash to the real color a few frames
   *  later. Optional — when omitted, the panel keeps the previous
   *  'blue'-until-bot-loads behavior (used by ChatPage routes where the
   *  agent isn't known until the conversation hydrates). */
  initialAgentColor?: string;
  branchPointMessageId?: string;
  onBranchCreated?: (result: BranchResult, mode: 'split' | 'navigate') => void;
  isBranchPanel?: boolean;
  isSplitView?: boolean;
  panelId: string;
  /** When set, the chat header shows this string instead of the bot name.
      Used by the branch rail to label non-main panels ("Branch 1", …). */
  panelLabel?: string;
  onNavigate?: (path: string) => void;
  onClose?: () => void;
  /** Scroll to and ring this message once it exists — how a teleport lands on
   *  the twin handoff card. Set by ChatPage on the panel owning the target. */
  focusMessageId?: string;
  /** Bumped per request so repeat jumps to the SAME message still re-fire. */
  focusNonce?: number;
  /** Optional extra content rendered between the chat header and the messages area (e.g. FlowProgressBar) */
  headerExtra?: React.ReactNode;
  /** Message to auto-send as soon as the conversation is ready (survives remounts). */
  autoSendMessage?: string;
  /** Optional model override paired with autoSendMessage. */
  autoSendModel?: string;
  /** When true, the per-message fork button is rendered disabled with a
   *  tooltip explaining why. Driven by ChatPage when the visible-panel cap
   *  has been reached. */
  branchDisabled?: boolean;
  /** Tooltip / aria text for the disabled fork button. */
  branchDisabledReason?: string;
  /** Start this conversation inside a shared project. Set by ChatPage from
   *  `?project=` when the user picked an agent on a project page. The thread is
   *  created lazily on the first send, so this has to survive until then. */
  projectId?: string;
  /** For a NEW project thread: who may write once it exists. */
  threadMode?: 'group' | 'solo';
}

export function ConversationPanel({
  initialConversationId,
  initialBotId,
  initialMessages,
  initialConversation,
  initialAgentColor,
  branchPointMessageId,
  onBranchCreated,
  isBranchPanel = false,
  isSplitView = false,
  panelId,
  panelLabel,
  autoSendMessage,
  autoSendModel,
  onNavigate: _onNavigate,
  onClose,
  focusMessageId,
  focusNonce,
  headerExtra,
  branchDisabled = false,
  branchDisabledReason,
  projectId,
  threadMode,
}: ConversationPanelProps) {
  const { t } = useLanguage();
  // Needed for the optimistic message's author tag in a shared project —
  // without it your own message is unlabelled until the next refetch, which
  // is precisely when you most want to see who is saying what.
  const { user: signedInUser } = useAuth();
  /* White-label gate — when off, the agent tint (user bubble, header,
     background) defers to the original neutral chat styling. M² preset
     turns this on; brij + default brands keep their existing look. */
  const brand = useBrand();
  const tintByAgent = !!brand.chat?.tintByAgent;
  const modeSliderEnabled = useFeature('agents.behavior_switching');
  const toolInvocationsEnabled = useFeature('observability.tool_invocations');
  // The conversation's feedback (ratings, skill corrections) is read only when
  // the tenant shows either — the endpoint answers 403 otherwise, and both
  // default off, so every conversation open used to log one.
  const messageRatingEnabled = useFeature('agents.message_rating');
  const skillFeedbackEnabled = useFeature('skills.activation_feedback');

  // Pending message to auto-send once the conversation is ready.
  // Prefer the prop (survives StrictMode remounts and page refreshes); fall
  // back to sessionStorage for legacy entry points. Never clear sessionStorage
  // here — the auto-send effect clears it after a successful send so the
  // message can't get lost on remount.
  const getPendingMessage = () => {
    if (isBranchPanel) return { message: null as string | null, model: null as string | null };
    if (autoSendMessage) {
      return { message: autoSendMessage, model: autoSendModel || null };
    }
    const message = sessionStorage.getItem('pendingChatMessage');
    const model = sessionStorage.getItem('pendingChatModel');
    return { message, model };
  };

  const pendingData = getPendingMessage();
  const messageParam = pendingData.message;
  const modelParam = pendingData.model;

  // Check module-level cache ONCE at mount time to avoid loading flash on remount
  const cachedEntry = initialConversationId ? _panelCache.get(initialConversationId) : undefined;
  const isCacheHit = !!cachedEntry;

  // State — pre-populate from cache when available to prevent loading flash
  const [conversation, setConversation] = useState<Conversation | null>(() => {
    if (initialConversation) return initialConversation;
    if (cachedEntry) return cachedEntry.conversation;
    return null;
  });
  // A support report sent from here names this conversation's agent.
  useSupportConversation(conversation?.conversation_id);
  const [messages, setMessages] = useState<Message[]>(initialMessages || []);
  const [bot, setBot] = useState<Bot | null>(() => {
    if (cachedEntry) return cachedEntry.bot;
    // Branch panels mount with `initialConversation` already set; if its bot
    // is in the shared cache, hydrate synchronously so the mode slider
    // renders on first paint instead of flickering in after the fetch.
    const chatbotId = initialConversation?.chatbot_id;
    if (chatbotId && _botCache.has(chatbotId)) return _botCache.get(chatbotId)!;
    return null;
  });
  const [botSkills, setBotSkills] = useState<Array<{ skill_id: string; skill_name: string }>>([]);
  const [activeModeIndex, setActiveModeIndex] = useState<number>(() => {
    // Resolution order:
    //   1. The conversation's saved active_mode_index (user explicitly chose
    //      a mode at some earlier turn — honor it). Branches inherit this
    //      from the parent at create-time, so this also covers the branch
    //      case.
    //   2. The bot's mode_slider_config.default_index — what the agent
    //      author marked as the "default" in BotModal.
    //   3. 0 — last-resort fallback when neither is known yet.
    //
    // Conversation/bot are pulled from `cachedEntry` (the panel cache) when
    // present, falling back to `initialConversation` (branch panels) and
    // the shared bot cache. Without the cachedEntry path, a fresh
    // conversation that just navigated to /chat?conversation=<id> hydrates
    // bot+conversation correctly from the panel cache (see useState calls
    // above) but lands on activeModeIndex=0 regardless of the bot's
    // default_index — visible as the slider showing the wrong mode on
    // first paint of a new conversation.
    const conv = cachedEntry?.conversation || initialConversation;
    const savedIdx = (conv as any)?.conversation_context?.active_mode_index;
    if (typeof savedIdx === 'number') return savedIdx;
    const cachedBot = cachedEntry?.bot
      || (initialConversation?.chatbot_id ? _botCache.get(initialConversation.chatbot_id) : undefined);
    const modeConfig = (cachedBot as any)?.mode_slider_config;
    return modeConfig?.default_index ?? 0;
  });
  const [skillFeedbackMap, setSkillFeedbackMap] = useState<Record<string, { expected_skills: Array<{ skill_id: string; skill_name: string }>; rating?: 'like' | 'dislike' | null; reasons?: string[]; feedback_description?: string | null }>>({});
  const [loading, setLoading] = useState(!initialConversation && !isCacheHit);
  const [sending, setSending] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);

  /**
   * Live refresh for SHARED PROJECT threads.
   *
   * A project conversation has several people in it, so the transcript on your
   * screen goes stale the moment someone else writes — you would sit looking at
   * a conversation that has moved on, and only find out by reloading. That is
   * exactly what was reported: "I can't see the messages other people sent".
   *
   * Polling, not sockets: the app has no realtime transport, and adding one for
   * this is a large piece of infrastructure. A few-second poll on a thread you
   * are actually looking at is cheap and behaves correctly on reconnect.
   *
   * Three things it must not do, which is most of the code below:
   *   - never run on an ordinary one-person chat (nothing can change under you);
   *   - never run while THIS panel is streaming, or a poll would land mid-turn
   *     and fight the optimistic bubbles;
   *   - never drop a local message that has no server id yet.
   */
  const isProjectThread = !!conversation?.project_id;

  /**
   * A SOLO thread is readable by the whole project and writable only by the
   * person who opened it. The server enforces that; this is what makes the
   * rule visible, because a send button that fails with a 403 is a worse
   * explanation than a sentence saying who this thread belongs to.
   */
  const isReadOnlyThread = (
    isProjectThread
    && conversation?.thread_mode === 'solo'
    && !!signedInUser?.user_id
    && conversation?.user_id !== signedInUser.user_id
  );
  /**
   * "Someone else is mid-turn" — derived from the transcript, not from a
   * presence service. If the newest message is a user message written by
   * SOMEBODY ELSE and no assistant reply has landed yet, the agent is
   * answering them right now. That is the group-chat cue: you can see the room
   * is busy without anyone publishing typing events.
   */
  const liveTurn = useMemo(() => {
    if (!isProjectThread || isStreaming) return null;
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'user') return null;
    if (!last.sender_user_id || last.sender_user_id === signedInUser?.user_id) return null;
    return last.sender_name || last.sender_email || null;
  }, [isProjectThread, isStreaming, messages, signedInUser?.user_id]);
  useEffect(() => {
    const conversationId = conversation?.conversation_id;
    // `isStreaming` alone was not enough: a send is in flight before the first
    // chunk arrives, and a poll landing in that window is exactly when the
    // transcript used to be clobbered.
    if (!isProjectThread || !conversationId || isStreaming || sending) return;
    if (typeof document !== 'undefined' && document.hidden) return;

    let cancelled = false;
    const tick = async () => {
      // A background tab is not being read; don't poll it.
      if (document.hidden) return;
      try {
        const fresh = await conversationService.getMessages(conversationId);
        if (cancelled || !Array.isArray(fresh)) return;
        setMessages(prev => {
          // Two rules, and between them the transcript cannot go wrong.
          //
          // ONE: never merge while a turn of OUR OWN is unresolved. A pending
          // send is on screen as `temp-` placeholders, and the server's copy of
          // that same turn carries a real id — so a merge here appends a second
          // bubble for a message already visible, which is how a lone reply
          // became two "Thinking…" bubbles. Our own turn settles in a few
          // seconds and the next tick picks everything up.
          if (prev.some(m => String(m.message_id).startsWith('temp-'))) return prev;

          // TWO: STRICTLY ADDITIVE. The first version of this replaced the list
          // with the server's copy, and that made whole conversations vanish:
          // any poll landing while a turn was still being persisted — or that
          // came back short for any reason — overwrote a good transcript with a
          // worse one. A background refresh must never be able to remove a
          // message from the screen, so we only ever APPEND ids we do not
          // already hold. An edit or deletion elsewhere corrects itself on the
          // next full load; a transcript that disappears under someone
          // mid-sentence does not.
          if (!fresh.length) return prev;
          const known = new Set(prev.map(m => m.message_id));
          const additions = fresh.filter(m => !known.has(m.message_id));
          return additions.length ? [...prev, ...additions] : prev;
        });
      } catch {
        // A failed poll is not worth surfacing: the next one will catch up,
        // and an error toast every few seconds on a flaky network is worse
        // than a transcript that lags briefly.
      }
    };

    const id = window.setInterval(tick, 4000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [isProjectThread, conversation?.conversation_id, isStreaming, sending]);
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [initialMessage, setInitialMessage] = useState<string>('');
  const [allModels, setAllModels] = useState<CatalogModel[]>([]);
  // The facts the picker's starting model is derived from, whatever order
  // they arrive in (utils/conversationModel.startingModel): the
  // conversation's own model, and whether the catalog has answered.
  const [conversationModel, setConversationModel] = useState<string | null>(null);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  // Once the person picks a model in this conversation, nothing re-derives it.
  const userPickedRef = useRef(false);
  const [filteredModels, setFilteredModels] = useState<CatalogModel[]>([]);
  // Mobile model picker removed — model selection now inside ChatInput
  const [editingMessage, setEditingMessage] = useState<Message | null>(null);
  /** The message a handoff would branch from. Non-null = the agent chooser is
   *  open; we keep the message rather than a boolean so the branch point stays
   *  pinned to what the user clicked, even if new messages stream in behind
   *  the modal. */
  const [agentPickerMessage, setAgentPickerMessage] = useState<Message | null>(null);
  const [editInputValue, setEditInputValue] = useState<string>('');
  const [editTimestamp, setEditTimestamp] = useState<number>(0);
  const [showBotInfo, setShowBotInfo] = useState(false);
  const [activeSkills, setActiveSkills] = useState<string[]>([]);
  const [thinkingSteps, setThinkingSteps] = useState<ThinkingStep[]>([]);
  // ChatGPT-style typing indicator state. Backend emits a `thinking` SSE
  // event with phase=uploading/opening/preparing/working/thinking as the
  // file flows through upload → save → LLM call. Defaults to 'thinking'
  // so the indicator works even on text-only sends.
  //
  // Status updates (phases AND the new `progress` events) can fire WITHIN
  // ~50ms of each other — faster than the eye can read. Every update flows
  // through ONE latest-wins queue: each shown status holds for at least
  // MIN_PHASE_VISIBLE_MS; while a slot is busy only the LATEST pending
  // update is kept (intermediates drop), so a chatty progress stream can
  // never build a backlog that replays after the turn ends.
  // 700ms was long enough to be *legible* but not long enough to be *readable*:
  // a turn with several tool rounds flashed a stack of different sentences in a
  // couple of seconds, which reads as noise rather than progress. Holding each
  // one longer doesn't hide anything — the queue is latest-wins, so the extra
  // dwell simply drops more intermediate states instead of showing them.
  const MIN_PHASE_VISIBLE_MS = 1800;
  // Breathing gap between the LAST revealed answer character and a parked
  // status appearing below it. Without it the status lands on the very next
  // animation frame after the text finishes — technically sequential, but
  // close enough to read as "status while it's still typing".
  const STATUS_AFTER_TEXT_DELAY_MS = 250;
  const [thinkingPhase, setThinkingPhase] = useState<ThinkingPhaseLit>('thinking');
  const [thinkingFilename, setThinkingFilename] = useState<string | undefined>(undefined);
  // The persistent live status (drives the status line that SURVIVES token
  // arrival). `since` is when this exact status was first shown — the
  // elapsed counter keys off it.
  const [liveStatus, setLiveStatus] = useState<(LiveStatusEntry & { since: number }) | null>(null);
  // Refs that drive the latest-wins queue:
  // - statusShownAtRef: timestamp when the currently-visible status was applied
  // - statusPendingRef: the single LATEST pending update waiting for its slot
  // - statusFlushTimerRef: handle to the next scheduled state update
  const statusShownAtRef = useRef<number>(0);
  const statusPendingRef = useRef<LiveStatusEntry | null>(null);
  const statusFlushTimerRef = useRef<number | null>(null);
  // When the typewriter last finished revealing text. A status arriving
  // within STATUS_AFTER_TEXT_DELAY_MS of this moment is parked for the
  // remainder of the gap — otherwise a tiny final fragment that reveals in
  // a single frame lets the status land ~1 frame after the last character,
  // which still reads as "status while it's typing".
  const lastTextSettledAtRef = useRef<number>(0);

  // Show a status NOW. A re-announced identical status keeps its original
  // `since` so the elapsed counter keeps ticking instead of resetting.
  const applyLiveStatus = (entry: LiveStatusEntry) => {
    statusShownAtRef.current = Date.now();
    if (entry.kind === 'phase') {
      setThinkingPhase(entry.phase);
      if (entry.filename !== undefined) setThinkingFilename(entry.filename);
    }
    setLiveStatus(prev => {
      if (!prev || !liveStatusEntriesEqual(prev, entry)) return { ...entry, since: Date.now() };
      // The same status again. A counting status ("Writing the document…
      // N words") is equal by design so it keeps its `since` and does not
      // cross-fade — but its number must still reach the screen.
      const sameParams = JSON.stringify((prev as { params?: unknown }).params ?? {})
        === JSON.stringify((entry as { params?: unknown }).params ?? {});
      return sameParams ? prev : { ...entry, since: prev.since };
    });
  };

  // Is the typewriter still revealing buffered answer text on screen? While
  // it is, NO status sentence may appear — visually the agent is "talking",
  // even if the network chunks already stopped. (streamAnimRef is declared
  // with the animator below; only ever called from handlers, so the later
  // declaration is safe.)
  const isStreamTextRevealing = () => {
    const anim = streamAnimRef.current;
    return !!anim && anim.raf !== null;
  };

  const flushPendingStatus = () => {
    statusFlushTimerRef.current = null;
    if (isStreamTextRevealing()) return; // parked — released on animator catch-up
    const next = statusPendingRef.current;
    statusPendingRef.current = null;
    if (next) applyLiveStatus(next);
  };

  // Public entry point used by the SSE handlers (phases + progress + text).
  const enqueueLiveStatus = (entry: LiveStatusEntry) => {
    // Text is visibly typing out — park the status; the animator's
    // caught-up branch releases it after the breathing gap.
    if (isStreamTextRevealing()) {
      statusPendingRef.current = entry;
      return;
    }
    // Text JUST finished revealing — enforce the breathing gap even though
    // the animator is already idle (a tiny final fragment can reveal in a
    // single frame, making the animator look idle the instant the status
    // event arrives).
    const sinceSettle = Date.now() - lastTextSettledAtRef.current;
    if (sinceSettle < STATUS_AFTER_TEXT_DELAY_MS) {
      statusPendingRef.current = entry;
      if (statusFlushTimerRef.current === null) {
        statusFlushTimerRef.current = window.setTimeout(
          flushPendingStatus, STATUS_AFTER_TEXT_DELAY_MS - sinceSettle
        );
      }
      return;
    }
    const sinceShown = Date.now() - statusShownAtRef.current;
    if (statusPendingRef.current === null && sinceShown >= MIN_PHASE_VISIBLE_MS) {
      // Free slot — show right away.
      applyLiveStatus(entry);
      return;
    }
    // Slot busy: keep only the LATEST pending update (intermediates drop) and
    // schedule a flush at the next free slot.
    statusPendingRef.current = entry;
    if (statusFlushTimerRef.current === null) {
      const wait = Math.max(0, MIN_PHASE_VISIBLE_MS - sinceShown);
      statusFlushTimerRef.current = window.setTimeout(flushPendingStatus, wait);
    }
  };

  // Hard clear — ai_complete / done / error / cleanup. Nothing may linger.
  const clearLiveStatus = () => {
    turnHasAnswerTextRef.current = false;
    if (statusFlushTimerRef.current !== null) {
      window.clearTimeout(statusFlushTimerRef.current);
      statusFlushTimerRef.current = null;
    }
    statusPendingRef.current = null;
    setLiveStatus(null);
  };

  // The moment answer text arrives, the model is TALKING — no status may
  // stay on screen next to a streaming reply. Clears the status line; the
  // next tool/artifact event re-shows it when real background work resumes
  // (which never overlaps with token flow in the ADK loop). Also resets the
  // min-visible clock so that next status appears instantly instead of
  // waiting out a stale 700ms slot.
  const clearLiveStatusOnText = () => {
    clearLiveStatus();
    statusShownAtRef.current = 0;
  };

  // Idle watchdog — "the screen must never sit motionless while the turn is
  // still running." Two real silent windows exist that no discrete event
  // narrates: the model pausing mid-stream between chunks (seconds of SSE
  // silence), and the tail between the last revealed text and the backend's
  // ai_complete/done (message persist + title/summary work). If the stream
  // has been quiet, nothing is revealing, and no status is up, surface an
  // honest "Thinking…" pulse until real activity resumes.
  // How long the stream must be silent before falling back to a generic
  // "thinking" line. At 600ms this fired in the natural gaps BETWEEN real
  // phases, so the user saw specific-status → thinking → specific-status
  // flicker. The fallback only earns its place once a silence is long enough
  // to actually feel like a stall.
  const STREAM_IDLE_STATUS_MS = 1500;
  const lastStreamSignalRef = useRef<number>(0);
  const idleWatchdogRef = useRef<number | null>(null);
  /** True once this turn has put answer text on screen. The idle fallback is
   *  for "nothing is visibly happening yet" — once the agent is demonstrably
   *  writing, a pause between chunks is just generation, and narrating it
   *  fills the natural gaps in a plain text reply with "Thinking…". */
  const turnHasAnswerTextRef = useRef(false);

  const noteStreamSignal = () => {
    lastStreamSignalRef.current = Date.now();
  };

  const stopIdleWatchdog = () => {
    if (idleWatchdogRef.current !== null) {
      window.clearInterval(idleWatchdogRef.current);
      idleWatchdogRef.current = null;
    }
  };

  const startIdleWatchdog = () => {
    stopIdleWatchdog();
    lastStreamSignalRef.current = Date.now();
    idleWatchdogRef.current = window.setInterval(() => {
      if (Date.now() - lastStreamSignalRef.current < STREAM_IDLE_STATUS_MS) return;
      if (isStreamTextRevealing()) return;                       // text still typing out
      if (statusPendingRef.current !== null) return;             // a status is on its way
      // Respect the breathing gap after the last revealed character.
      if (Date.now() - lastTextSettledAtRef.current < STATUS_AFTER_TEXT_DELAY_MS) return;
      // Once the answer has started, stay out of the way. Real tool rounds
      // still surface, because those arrive as explicit phase/progress events
      // through applyLiveStatus — this watchdog only invents a generic
      // "Thinking…" out of silence, and after text that silence is meaningless.
      if (turnHasAnswerTextRef.current) return;
      setLiveStatus(prev => {
        if (prev) return prev;                                   // something already showing
        statusShownAtRef.current = Date.now();
        return { kind: 'phase', phase: 'thinking', since: Date.now() };
      });
    }, 300);
  };

  // On unmount, clear any pending timers.
  useEffect(() => () => {
    if (statusFlushTimerRef.current !== null) {
      window.clearTimeout(statusFlushTimerRef.current);
      statusFlushTimerRef.current = null;
    }
    stopIdleWatchdog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolved sentence for the status line (resolved at render time so a
  // mid-stream language switch re-translates). Strictly event-driven from
  // the parked/latest-wins queue — the legacy artifactBuild cycling ticker
  // is NOT used here: it was set synchronously on tool_debug and would leak
  // past the typewriter parking, showing a sentence beside still-revealing
  // answer text. Explicit artifact_progress stage events cover the chat
  // line; the artifact panel keeps its own cycling sentences.
  const liveStatusText = liveStatus ? resolveLiveStatusLabel(t, liveStatus) : undefined;
  const [scrollBtn, setScrollBtn] = useState<'up' | 'down' | null>(null);
  // AgentDebugPanel state — when observability.tool_invocations is on, the
  // toggle in the chat header mounts AgentDebugPanel which subscribes to
  // these four streams. The setters were already populated by the SSE
  // event handler; we just stopped reading them when the panel was removed.
  const [debugEvents, setDebugEvents] = useState<ToolDebugEvent[]>([]);
  const [activeSkillEntries, setActiveSkillEntries] = useState<ActiveSkillEntry[]>([]);
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);
  // Agent output artifacts — live right-side panel. Gated on modules.agent_outputs.
  const agentOutputsEnabled = useFeature('modules.agent_outputs');
  const artifacts = useArtifacts();

  // ─── Artifact branding toggles (composer) ───────────────────────────────
  // The switch popover exists only when toggling it would change anything:
  // the tenant has a live brand book (server-checked once — the endpoint
  // verifies both the design-skill bundle and its feature flag), the agent
  // has pptx/docx output formats on, and a conversation exists to hold the
  // per-conversation override. Absent key = branded; default is ON.
  const [brandedAvailable, setBrandedAvailable] = useState(false);
  const [artifactBranding, setArtifactBranding] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (!agentOutputsEnabled) return;
    let cancelled = false;
    api.get('/conversations/artifact-brand-status')
      .then(res => { if (!cancelled) setBrandedAvailable(!!res.data?.branded_available); })
      .catch(() => { if (!cancelled) setBrandedAvailable(false); });
    return () => { cancelled = true; };
  }, [agentOutputsEnabled]);
  const wikiFeatureEnabled = useFeature('modules.wiki');
  const wikiGraph = useWikiGraph(conversation?.conversation_id);

  // ─── Unified right side panel ───────────────────────────────────────────
  // Artifacts and the Wiki graph share ONE dock with a tab strip — never two
  // stacked panels. `sideView` is which tab is showing; it AUTO-JUMPS to
  // whichever the agent is actively producing (artifact events → 'artifacts',
  // wiki tool calls → 'wiki'), and the user can switch manually any time.
  const [sideView, setSideView] = useState<'artifacts' | 'wiki' | 'drive'>('artifacts');
  // Live Google Drive edits made by the agent in this conversation — the
  // "Drive" side tab shows the file in Google's own editor plus each step
  // with an Undo. Hydrated from the API on load, appended by `drive_edit`
  // events during a turn.
  const [driveEdits, setDriveEdits] = useState<DriveEdit[]>([]);
  // A Drive tool in the running turn is waiting for more Google access.
  const turnAccess = useTurnGoogleAccess(isStreaming);
  // A per-person connector's tool waits for the sender to connect their own account (item 65A).
  const turnConnector = useTurnConnectorAccess(isStreaming);
  const [driveActiveFileId, setDriveActiveFileId] = useState<string | null>(null);
  // Only asked for on tenants where the connector exists AND this person has
  // actually connected Drive — otherwise opening a conversation costs exactly
  // the requests it cost before.
  const cloudStorageOn = useFeature('integrations.cloud_storage');
  const driveEditsAvailable = useDriveConnected(cloudStorageOn);
  // The composer's connectors control exists only for an agent that ALLOWS
  // Google Drive in a tenant that has it; each person then switches it on for
  // themselves there (AgentConnectors — the server's answer is the last word).
  const driveSwitchAgentId = cloudStorageOn && bot?.handler_config?.connectors?.google_drive === true
    ? bot.chatbot_id : undefined;
  // …and for an agent whose connectors include one each person signs in to
  // (item 65A) — the control itself asks, and renders nothing when there is none.
  const mcpConnectorsOn = useFeature('integrations.mcp_connectors');
  const apiConnectorsOn = useFeature('integrations.api_connectors');
  const connectorsAgentId = driveSwitchAgentId
    ?? ((mcpConnectorsOn || apiConnectorsOn) && bot?.chatbot_id ? bot.chatbot_id : undefined);
  const loadDriveEdits = useCallback(async (conversationId?: string | null) => {
    if (!conversationId || !driveEditsAvailable) { setDriveEdits([]); return; }
    try {
      const edits = await driveService.listEdits(conversationId);
      setDriveEdits(edits);
      if (edits.length) setDriveActiveFileId((cur) => cur && edits.some((e) => e.file_id === cur) ? cur : edits[edits.length - 1].file_id);
    } catch { setDriveEdits([]); }
  }, [driveEditsAvailable]);
  const handleDriveRevert = useCallback(async (editId: string) => {
    if (!conversation?.conversation_id) return;
    const result = await driveService.revertEdit(conversation.conversation_id, editId);
    setDriveEdits(result.edits);
  }, [conversation?.conversation_id]);
  const [sideCollapsed, setSideCollapsed] = useState(false);
  // ONE width for the whole unified panel — switching tabs never resizes the
  // dock; either view's drag handle adjusts this same shared width. The clamp
  // lives HERE (the single setter) so no drag handler can push an unbounded /
  // negative width into state or localStorage. Persisted across reloads.
  const SIDE_PANEL_MIN = 340;
  const SIDE_PANEL_MAX = 760;
  const [sideWidth, setSideWidthRaw] = useState<number>(() => {
    const saved = Number(localStorage.getItem('chat.sidePanel.width'));
    return Number.isFinite(saved) && saved >= SIDE_PANEL_MIN && saved <= SIDE_PANEL_MAX ? saved : 460;
  });
  const setSideWidth = useCallback((w: number) => {
    setSideWidthRaw(Math.max(SIDE_PANEL_MIN, Math.min(SIDE_PANEL_MAX, Math.round(w))));
  }, []);
  useEffect(() => {
    try { localStorage.setItem('chat.sidePanel.width', String(Math.round(sideWidth))); } catch { /* ignore */ }
  }, [sideWidth]);

  // Forward artifact lifecycle events to the panel. The chat status line is
  // driven separately by the explicit progress / artifact_progress events
  // (through the parked latest-wins queue), never directly from here.
  const handleArtifactEvent = useCallback((event: any) => {
    if (event?.type === 'artifact_start') {
      // Agent started building an artifact → surface the Artifacts tab,
      // expanding the panel if it was collapsed to the rail.
      setSideView('artifacts');
      setSideCollapsed(false);
    }
    artifacts.handleEvent(event);
  }, [artifacts]);
  // Measure the chat header so the docked artifact panel starts BELOW it (it
  // occupies the chat section, like the AoA inspector — header stays full-width).
  const chatHeaderRef = useRef<HTMLDivElement | null>(null);
  const [chatHeaderH, setChatHeaderH] = useState(0);
  useEffect(() => {
    const el = chatHeaderRef.current;
    if (!el || !agentOutputsEnabled) return;
    const update = () => setChatHeaderH(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [agentOutputsEnabled, artifacts.activeId]);
  const { loadArtifact: loadArtifactDetail } = artifacts;
  // On (re)load, register any agent-generated artifacts found on this
  // conversation's messages so the panel can show them. Does not force the
  // panel open — the user opens it from an attachment or it auto-opens live.
  const seenArtifactIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!agentOutputsEnabled) return;
    for (const m of messages) {
      for (const att of (m.attachments || [])) {
        const id = (att as any).artifact_id || ((att as any).is_agent_generated ? att.file_id : null);
        if (id && !seenArtifactIdsRef.current.has(id)) {
          seenArtifactIdsRef.current.add(id);
          void loadArtifactDetail(id);
        }
      }
    }
  }, [messages, agentOutputsEnabled, loadArtifactDetail]);
  const messageCountRef = useRef(0);
  const [pageDragging, setPageDragging] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[] | null>(null);
  const [quotedText, setQuotedText] = useState<string | null>(null);

  // E1 (no models available for this agent): replace chat input with a
  // recoverable error panel + Refresh button.
  const [noModelsAvailable, setNoModelsAvailable] = useState(false);

  // Load failure recovery: when the initial GET /conversations/<id> +
  // GET /messages pair fails (network flake, 30s axios timeout, malformed
  // response), we auto-retry once silently. If that also fails we show an
  // inline error panel with a manual Retry button instead of a blank page
  // or an indefinite spinner. `loadRetryNonce` bumps to re-run the init
  // effect when the user clicks Retry.
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadRetryNonce, setLoadRetryNonce] = useState(0);
  // The conversation names an agent that could not be loaded (deleted, or no
  // access). Ends the composer's wait for the agent — see `agentPending`.
  const [agentUnavailable, setAgentUnavailable] = useState(false);

  // Refs
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Track per-conversation toast suppression so the model-substituted
  // warning only appears once per session per conversation (R3).
  const modelSubstitutedToastedRef = useRef<Set<string>>(new Set());
  const scrollBtnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastScrollTopRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  // The paused answer a running Continue is growing (null otherwise).
  const continuingIdRef = useRef<string | null>(null);
  // Single-shot timer for the post-Stop reconcile in handleStopGeneration.
  // A ref so a second Stop, a conversation switch, or unmount cancels the
  // pending re-read rather than letting a stale verdict land on a
  // transcript it no longer describes.
  const stopReconcileTimerRef = useRef<number | null>(null);
  /** Bumped by every Stop and every conversation switch: a re-read still
   *  running for an earlier one lands nothing. */
  const stopReconcileSeqRef = useRef(0);
  const activeSkillsRef = useRef<string[]>([]);
  const hasAutoSentRef = useRef(false);
  const sendMessageRef = useRef<((content: string, attachments?: File[], driveFiles?: DriveFileRef[]) => Promise<SendOutcome>) | null>(null);

  // Conversation token-budget guard (Change C). One banner state per
  // conversation; cleared on conversation switch + on send-success. The
  // warning banner is dismissible (the user can hide it for the session);
  // the full banner blocks further sends.
  const [conversationBanner, setConversationBanner] = useState<
    | {
        kind: 'warning';
        message: string;
      }
    | {
        kind: 'full';
        message: string;
      }
    | null
  >(null);

  // The context meter: how full this conversation is against the model's
  // window. Seeded from GET /conversations/<id>, re-read when the picker
  // changes (a different window), and kept current by the stream's
  // `context_usage` events. `compacting` drives the meter's spinner while a
  // manual or automatic compaction runs.
  const [contextUsage, setContextUsage] = useState<ContextUsage | null>(null);
  const [compacting, setCompacting] = useState(false);

  /**
   * Put a compaction marker into the transcript at the cut — after the last
   * folded row, which is the end of the transcript so far (the new message
   * of this turn, if already on screen, stays below it). The folded rows
   * stay where they are (nothing is deleted); an earlier marker is hidden
   * because the new summary folded it too.
   */
  const insertCompactionMarker = useCallback((marker: BackendMessage, foldedIds: string[]) => {
    const info = compactionInfoOf(marker);
    if (!info) return;
    const row: Message = {
      message_id: marker.message_id,
      conversation_id: marker.conversation_id,
      role: 'system',
      content: marker.message_content,
      created_at: marker.created_at,
      attachments: [],
      compaction: { ...info, superseded: false },
      render_key: 'compaction-live',
    };
    setMessages((prev) => {
      if (prev.some((m) => m.message_id === row.message_id)) return prev;
      const folded = new Set(foldedIds);
      const next = prev.map((m) =>
        m.compaction && !m.compaction.superseded
          ? { ...m, compaction: { ...m.compaction, superseded: true } }
          : m,
      );
      let at = -1;
      next.forEach((m, i) => { if (folded.has(m.message_id)) at = i; });
      if (at < 0) {
        // No folded row on screen (a reload mid-way) — place it by time.
        at = next.findIndex((m) => m.created_at && row.created_at && m.created_at > row.created_at) - 1;
        if (at < -1) at = next.length - 1;
      }
      next.splice(at + 1, 0, row);
      return next;
    });
  }, []);

  /** "Compact now" from the meter's popover. */
  const handleCompactNow = useCallback(async () => {
    if (!conversation?.conversation_id || compacting) return;
    setCompacting(true);
    try {
      const result = await conversationService.compactNow(conversation.conversation_id, selectedModel || undefined);
      if (result.usage) setContextUsage(result.usage);
      if (!result.compacted) {
        antdMessage.info(t('chat.context.nothingToCompact', {
          defaultValue: 'Nothing to compact yet — the conversation is still short.',
        }));
        return;
      }
      if (result.summary_message) {
        insertCompactionMarker(result.summary_message, result.folded_message_ids || []);
      }
      antdMessage.success(t('chat.context.compactedToast', {
        count: result.summarized_count ?? 0,
        defaultValue: `Context compacted — ${result.summarized_count ?? 0} messages summarised.`,
      }));
    } catch (error) {
      logger.error('Compact now failed', error);
      antdMessage.error(t('chat.context.compactFailed', {
        defaultValue: "We couldn't compact this conversation right now. Try again in a moment.",
      }));
    } finally {
      setCompacting(false);
    }
  }, [conversation?.conversation_id, compacting, selectedModel, insertCompactionMarker, t]);

  // Re-band the meter when the picker changes: the same conversation is a
  // different share of a different window.
  useEffect(() => {
    const id = conversation?.conversation_id;
    if (!id || !selectedModel) return;
    let cancelled = false;
    conversationService.getContextUsage(id, selectedModel)
      .then((usage) => { if (!cancelled) setContextUsage(usage); })
      .catch(() => { /* the meter is advisory */ });
    return () => { cancelled = true; };
  }, [conversation?.conversation_id, selectedModel]);

  // Guard against React StrictMode double-invoking conversation creation
  const initializingBotIdRef = useRef<string | null>(null);
  // Messages the user sent while createConversationWithBot() was still in
  // flight or while another send was streaming (the input is live in both
  // windows). Parked here (FIFO — rapid sends must not overwrite each other)
  // and flushed one per completed send — never silently dropped.
  const pendingSendsRef = useRef<{ content: string; attachments?: File[]; driveFiles?: DriveFileRef[] }[]>([]);
  // Synchronous single-flight lock for handleSendMessage. The UI's
  // sendDisabled gate can't cover programmatic senders (auto-send +
  // parked-message flush can land on the same commit), and `sending` state is
  // stale within a tick — this ref is the authoritative in-flight marker.
  const sendInFlightRef = useRef(false);
  const pageDragCounterRef = useRef(0);
  const quoteBtnRef = useRef<HTMLButtonElement>(null);
  const pendingQuoteTextRef = useRef<string>('');

  // Broadcast streaming state so Sidebar can auto-collapse
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('streamingStateChanged', {
      detail: { isStreaming }
    }));
  }, [isStreaming]);

  // Retroactive tool-invocation hydration. When the user reloads an
  // existing conversation the SSE stream from the original turn is gone,
  // so we backfill the debug panel timeline from the persisted rows.
  // Only fires when the feature flag is on AND a conversation_id exists.
  // Skipped while still loading (initial GETs in flight) and during a
  // live send (would clobber the in-progress event stream).
  useEffect(() => {
    if (!toolInvocationsEnabled) return;
    const convId = conversation?.conversation_id;
    if (!convId) return;
    if (loading || isStreaming || sending) return;

    let cancelled = false;
    toolInvocationService
      .listForConversation(convId)
      .then((rows) => {
        if (cancelled) return;
        // Each persisted row becomes one call + one result event so the
        // panel renders the same way it does live.
        const events: ToolDebugEvent[] = [];
        for (const r of rows) {
          events.push({
            type: 'tool_debug',
            action: 'call',
            tool: r.tool_name,
            args: (r.input_json as Record<string, unknown>) || undefined,
            invocation_id: r.invocation_id,
            timestamp: r.started_at ? new Date(r.started_at).getTime() : undefined,
          });
          if (r.status !== 'pending') {
            // Format the output as a JSON preview when it's an object,
            // matching the shape the live stream uses.
            const out = r.output_json;
            const preview =
              out == null
                ? (r.error_text || '')
                : typeof out === 'string'
                  ? out
                  : JSON.stringify(out, null, 2);
            events.push({
              type: 'tool_debug',
              action: 'result',
              tool: r.tool_name,
              preview,
              invocation_id: r.invocation_id,
              timestamp: r.completed_at ? new Date(r.completed_at).getTime() : undefined,
            });
          }
        }
        setDebugEvents(events);
      })
      .catch((err) => {
        // Non-fatal — debug panel just stays empty.
        logger.debug('tool-invocations retroactive fetch failed:', err);
      });

    return () => { cancelled = true; };
  }, [toolInvocationsEnabled, conversation?.conversation_id, loading, isStreaming, sending]);

  // Keyboard-aware viewport — keeps the chat layout glued to the visible
  // area on mobile. iOS Safari doesn't resize 100vh when the keyboard
  // opens, and even `100dvh` is imprecise during the keyboard animation.
  // visualViewport.height is the exact visible height at every moment,
  // so we publish it as CSS variables that ChatPage.css reads. Unrelated
  // pages never set these variables — they stay undefined and the CSS
  // falls back to 100dvh.
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return;

    const root = document.documentElement;
    let raf = 0;

    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        root.style.setProperty('--chat-vvh', `${vv.height}px`);
        root.style.setProperty('--chat-vvtop', `${vv.offsetTop}px`);
      });
    };

    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      cancelAnimationFrame(raf);
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      root.style.removeProperty('--chat-vvh');
      root.style.removeProperty('--chat-vvtop');
    };
  }, []);

  // Broadcast the panel's agent color so the ChatPage branch switcher can tint itself.
  // Mirrors BotAvatar's logic — passes through resolveAgentTextureColor
  // so a HEX `avatar_color` (M² palette agents store hex) is mapped to
  // the matching TextureColor literal. Otherwise the broadcast would
  // carry a hex into ChatPage's data-agent-color and miss every
  // [data-agent-color="blue"|"red"|...] CSS rule, leaving the chat
  // surface tint stuck on its default.
  useEffect(() => {
    if (bot?.chatbot_name) {
      const resolved = resolveAgentTextureColor(bot.avatar_color, bot.chatbot_name);
      // Cache for instant correct color on the next entry to this agent.
      if (bot.chatbot_id) writeCachedAgentColor(String(bot.chatbot_id), resolved);
      window.dispatchEvent(new CustomEvent('panelBotLoaded', {
        detail: { panelId, avatarColor: resolved },
      }));
    }
  }, [bot?.avatar_color, bot?.chatbot_name, bot?.chatbot_id, panelId]);

  // Smooth streaming typewriter — word-boundary animation for ChatGPT-like flow
  const streamAnimRef = useRef<{
    base: string;         // Text the message already had (a Continue grows it)
    target: string;       // Full text received so far from SSE
    displayed: string;    // Text currently shown on screen
    msgId: string;        // The streaming AI message's id
    raf: number | null;   // requestAnimationFrame handle
    lastTime: number;     // Last animation timestamp
  } | null>(null);

  const startStreamAnim = (msgId: string, base = '') => {
    stopStreamAnim();
    streamAnimRef.current = { base, target: '', displayed: '', msgId, raf: null, lastTime: 0 };
  };

  const pushStreamText = (text: string) => {
    const anim = streamAnimRef.current;
    if (!anim) return;
    anim.target = text;
    if (anim.raf === null) {
      anim.lastTime = performance.now();
      anim.raf = requestAnimationFrame(tickStreamAnim);
    }
  };

  const tickStreamAnim = (now: number) => {
    const anim = streamAnimRef.current;
    if (!anim) return;

    const elapsed = now - anim.lastTime;
    anim.lastTime = now;

    const remaining = anim.target.length - anim.displayed.length;

    if (remaining <= 0) {
      // Caught up — pause animation until new text arrives.
      anim.raf = null;
      // Remember when the text settled: statuses arriving in the next
      // STATUS_AFTER_TEXT_DELAY_MS are parked for the remainder of the gap.
      lastTextSettledAtRef.current = Date.now();
      turnHasAnswerTextRef.current = true;
      // Release any status parked while the text was typing out — after a
      // short breathing gap, so the status never lands on the same frame as
      // the final characters. flushPendingStatus re-checks the animator, so
      // if new text resumes during the gap the status stays parked.
      if (statusPendingRef.current && statusFlushTimerRef.current === null) {
        statusFlushTimerRef.current = window.setTimeout(
          flushPendingStatus, STATUS_AFTER_TEXT_DELAY_MS
        );
      }
      return;
    }

    // Steady-rate reveal — no catch-up acceleration.
    //
    // We used to race ahead when the buffer was large (up to 400 chars/sec)
    // which made medium-length responses flash on-screen and feel
    // "one-shot" rather than streamed. Anthropic models (notably Sonnet)
    // emit 4–5 large chunks per turn with 400–600ms gaps, so the buffer
    // is often >80 chars at any given moment — the old catch-up branch
    // fired almost every frame and the user saw a sudden burst.
    //
    // A steady 150 chars/sec (~10 chars per 60fps frame) gives a
    // ChatGPT-style typing feel and stays comfortably ahead of the
    // server's chunk cadence: a typical 400-char Sonnet reply takes
    // ~3.5s to stream from the server and reveals in ~2.7s, so the
    // animation always finishes around the same time as ai_complete
    // (which then flushes any remainder via flushStreamAnim).
    const baseRate = 0.15;  // chars per ms (~150/sec, ~9 chars per 60fps frame)
    const budget = Math.max(1, Math.round(baseRate * elapsed));
    let end = anim.displayed.length + budget;

    // Extend to the next word boundary (space, newline, punctuation) so we
    // don't cut words in half — feels more natural.
    if (end < anim.target.length) {
      const slice = anim.target;
      while (end < slice.length && slice[end] !== ' ' && slice[end] !== '\n') {
        end++;
      }
    }

    const reveal = Math.min(end, anim.target.length) - anim.displayed.length;
    anim.displayed = anim.target.slice(0, anim.displayed.length + reveal);

    const snapshot = anim.base + anim.displayed;
    const id = anim.msgId;
    setMessages(prev => prev.map(m =>
      m.message_id === id ? { ...m, content: snapshot } : m
    ));

    anim.raf = requestAnimationFrame(tickStreamAnim);
  };

  const flushStreamAnim = () => {
    const anim = streamAnimRef.current;
    if (!anim) return;
    if (anim.raf !== null) cancelAnimationFrame(anim.raf);
    anim.raf = null;
    // Show all remaining text immediately
    if (anim.displayed !== anim.target) {
      const id = anim.msgId;
      const content = anim.base + anim.target;
      setMessages(prev => prev.map(m =>
        m.message_id === id ? { ...m, content } : m
      ));
      anim.displayed = anim.target;
    }
  };

  const stopStreamAnim = () => {
    const anim = streamAnimRef.current;
    if (anim?.raf !== null && anim) cancelAnimationFrame(anim.raf);
    streamAnimRef.current = null;
  };
  // justCreatedConversation guard is now module-level (_panelCache)
  // so it survives component remounts caused by key prop changes

  // Store pending message from URL on first mount only
  const pendingMessageRef = useRef<string | null>(messageParam);

  // Track if we've loaded models already (to prevent re-setting default)
  const hasLoadedModelsRef = useRef(false);
  // Default FAMILY key — always derived from the API response, never a
  // hardcoded concrete model id. Empty until /ai/models/ resolves.
  const defaultModelRef = useRef('');
  const defaultPerProviderRef = useRef<Record<string, string>>({});

  // Load bot skills for feedback feature
  useEffect(() => {
    if (!bot?.chatbot_id) { setBotSkills([]); return; }
    (async () => {
      try {
        const { SkillsApi } = await import('@modules/skills/services/skillsApi');
        const skills = await SkillsApi.getChatbotSkills(bot.chatbot_id, true);
        setBotSkills(skills.map(s => ({ skill_id: s.skill_id, skill_name: s.skill?.skill_name || s.skill_id })));
      } catch { setBotSkills([]); }
    })();
  }, [bot?.chatbot_id]);

  // Load all available models from API on mount
  useEffect(() => {
    const loadModels = async () => {
      try {
        // Named by the pins of the tenant this conversation runs on (a project
        // thread may be another tenant's) — utils/modelPicker `CatalogContext`.
        const data = await cachedGet<any>(
          modelCatalogUrl(null, { conversationId: initialConversationId }), 5 * 60_000);
        const models = data.models || [];
        // Keep the FULL family entry (resolved_model, capabilities, provider),
        // not just {id,name} — the picker shows the pinned release under the
        // family name and the send path reads the provider from here. No
        // hidden-model list: the endpoint already returns exactly the families
        // this tenant may use.
        const modelList: CatalogModel[] = models.map(toCatalogModel);
        setAllModels(modelList);
        setFilteredModels(modelList);

        // Store defaults from API — family keys, straight from the response.
        const perProvider = data.default_per_provider || {};
        defaultPerProviderRef.current = perProvider;
        // Prefer Anthropic default, then global default, then first family.
        // Never a hardcoded concrete id — the tenant decides what Sonnet runs.
        const apiDefault = perProvider.anthropic
          || data.default
          || modelList[0]?.id
          || '';
        defaultModelRef.current = apiDefault;

        setCatalogLoaded(true);
        if (!hasLoadedModelsRef.current) {
          hasLoadedModelsRef.current = true;
          if (modelParam) {
            // A model named in the URL is a choice — snap a legacy concrete
            // id to its family and keep it.
            userPickedRef.current = true;
            setSelectedModel(findModelEntry(modelList, modelParam)?.id || modelParam);
          }
        }
        // Otherwise the starting model is derived (the effect below) — never
        // set from here: this closure's `selectedModel` belongs to the render
        // that started the fetch, so a default written from it overwrote a
        // conversation's model whenever the catalog answered last (2026-10-19).
      } catch (error) {
        console.error('Failed to load models:', error);
      }
    };
    loadModels();

    // Store pending message from URL param on first load
    if (pendingMessageRef.current && !hasAutoSentRef.current) {
      logger.debug('Setting initial message from URL:', pendingMessageRef.current);
      setInitialMessage(pendingMessageRef.current);
    }
  }, [modelParam]);

  /**
   * Normalize any stored model ref (family key OR legacy concrete id like
   * 'claude-sonnet-4-5-20250929') to its family key via the loaded catalog.
   * Refs with no catalog match pass through unchanged (the backend
   * normalizes/substitutes authoritatively).
   */
  const toFamilyKey = (ref: string): string =>
    findModelEntry(allModels, ref)?.id || ref;

  /**
   * Catalog-driven name for any model ref (family key or concrete id) — the
   * RELEASE it is (a concrete id) or runs as (a family: its pin), never a raw
   * key (utils/modelLabels.refLabel). Passed to MessageList for the
   * model-switch dividers of a message the server did not name (an answer
   * carries `answered_by`, which wins); unmatched refs return undefined so
   * MessageList falls back to its legacy static map.
   */
  const resolveModelDisplayName = useCallback(
    (ref: string): string | undefined =>
      (findModelEntry(allModels, ref) ? ranLabel(allModels, ref) : undefined),
    [allModels],
  );

  /**
   * Canonical identity of a model ref: the catalogue entry's id. `sonnet` and
   * `claude-sonnet-4-5-20250929` both land on the same entry, so both answer
   * with the same identity. The model-switch divider compares this instead of
   * the raw ref — comparing raw refs drew a "Sonnet → Sonnet" divider on every
   * turn, because a live message carries the family key and a reloaded one
   * carries the concrete id.
   */
  const resolveModelIdentity = useCallback(
    (ref: string): string | undefined => findModelEntry(allModels, ref)?.id,
    [allModels],
  );

  /**
   * Sort models with default model first
   */
  const sortModelsWithDefaultFirst = (models: typeof allModels): typeof allModels => {
    const def = defaultModelRef.current;
    return [...models].sort((a, b) => {
      if (a.id === def) return -1;
      if (b.id === def) return 1;
      return 0;
    });
  };

  /**
   * Filter models when bot changes or allModels loads
   * Only validates/changes selectedModel when bot actually changes (not on every render)
   */
  useEffect(() => {
    if (!allModels.length) return;

    const allowedModels = bot?.handler_config?.allowed_models ||
                          bot?.conversation_capabilities?.allowed_models;

    let finalFilteredModels: typeof allModels;
    let allowedList: string[] | null = null;

    if (!bot || !allowedModels || allowedModels === 'all') {
      finalFilteredModels = allModels;
    } else {
      if (typeof allowedModels === 'string') {
        allowedList = allowedModels.split(',').map(m => m.trim()).filter(Boolean);
      } else if (Array.isArray(allowedModels)) {
        allowedList = allowedModels;
      } else {
        allowedList = [];
      }

      // Match at FAMILY level: a stored ref may be a family key, the pinned
      // version, or an older version of the family. Matching only the
      // CURRENT pin made an agent's Sonnet vanish from this picker the day
      // the tenant re-pinned Sonnet 4.6 → 5, and the picker fell to Gemini.
      const allowedFamilies = new Set(
        allowedList.map(ref => findModelEntry(allModels, ref)?.id).filter(Boolean) as string[],
      );
      const filtered = allModels.filter(m => allowedFamilies.has(m.id));
      finalFilteredModels = filtered.length > 0 ? filtered : allModels;
    }

    finalFilteredModels = sortModelsWithDefaultFirst(finalFilteredModels);
    setFilteredModels(finalFilteredModels);
  }, [bot, allModels]);

  /**
   * The picker's starting model — derived from what is known (the catalog,
   * the conversation's own model, the agent) and re-derived as each arrives,
   * in whatever order, until the person picks one
   * (utils/conversationModel.startingModel). The conversation's model wins
   * whenever it has one; a default never replaces it while the catalog cannot
   * yet name its family. A model the agent does not allow falls back to the
   * agent's default.
   */
  useEffect(() => {
    if (userPickedRef.current) return;
    const next = startingModel({
      resolve: catalogLoaded ? (ref: string) => findModelEntry(allModels, ref)?.id : null,
      conversationModel,
      agentDefault: bot?.default_model,
      allowed: bot ? getAllowedModelsList(bot) : null,
      tenantDefault: defaultModelRef.current,
    });
    if (next) setSelectedModel(next);
  }, [allModels, catalogLoaded, conversationModel, bot]);

  /**
   * The agent this panel talks to is still on its way: the panel is loading,
   * or it knows the agent's id (a new chat started from an agent, a
   * conversation that names one) but not the agent yet. Not tied to `loading`
   * alone: under StrictMode's double effect run `loading` clears before the
   * first run's agent fetch lands. A conversation whose agent cannot be loaded
   * stops waiting (`agentUnavailable`) and the composer is what it always was.
   */
  const agentPending = !bot && !agentUnavailable && (
    loading || !!(conversation?.chatbot_id || (!conversation && initialBotId))
  );

  /**
   * Get allowed models list from bot config
   */
  const getAllowedModelsList = (botData: Bot): string[] | null => {
    const allowedModels = botData.handler_config?.allowed_models ||
                          botData.conversation_capabilities?.allowed_models;

    if (allowedModels && allowedModels !== 'all') {
      if (typeof allowedModels === 'string') {
        return allowedModels.split(',').map(m => m.trim()).filter(Boolean);
      } else if (Array.isArray(allowedModels)) {
        return allowedModels;
      }
    }
    return null;
  };

  /**
   * The conversation's own model as its context reports it (the agent itself
   * reaches the picker through `bot` — see the derivation above). A model the
   * server already named for this load (its effective model) stands.
   */
  const adoptConversationContext = (conversationContext?: { current_model?: string }) => {
    const current = conversationContext?.current_model;
    if (current) setConversationModel(prev => prev || current);
  };

  /**
   * Re-fetch /ai/models/ to refresh the dropdown when the tenant's
   * allowed-models whitelist changed under us. Quietly no-ops on error
   * (the picker keeps its existing options).
   */
  const refreshAvailableModels = async () => {
    try {
      const response = await api.get(modelCatalogUrl(null, {
        conversationId: conversation?.conversation_id || initialConversationId,
      }));
      const models = response.data.models || [];
      setAllModels(models.map(toCatalogModel));
    } catch (e) {
      logger.debug('Failed to refresh /ai/models/ after substitution:', e);
    }
  };

  /**
   * Handle a server-side `model_substituted` signal. Updates the picker
   * to the effective model, refreshes the dropdown, and toasts once
   * per session per conversation (R3).
   */
  const handleModelSubstituted = (
    effectiveModel: string,
    opts: {
      sticky?: boolean; reason?: string; requested?: string;
      effectiveName?: string | null; requestedName?: string | null;
    } = {},
  ) => {
    if (!effectiveModel) return;
    // `sticky` (default): the agent itself now runs the effective model —
    // the picker follows and the agent's allowed list is re-read. A
    // transient provider failover (`sticky: false`) served this one reply
    // on another model; the picker keeps the user's choice.
    const sticky = opts.sticky !== false;
    const previousModel = opts.requested || selectedModel;
    if (sticky) {
      // The server may report either a family key or a concrete id — snap
      // the picker to the family, and toast with family display names. It is
      // the conversation's model from now on (the catalog refresh below
      // re-derives the picker from it).
      userPickedRef.current = false;
      setConversationModel(effectiveModel);
      setSelectedModel(toFamilyKey(effectiveModel));
      refreshAvailableModels();
      if (bot?.chatbot_id) {
        fetchBotShared(bot.chatbot_id)
          .then((fresh) => { if (fresh) setBot(fresh); })
          .catch((e) => logger.debug('Failed to refresh agent after substitution:', e));
      }
    }

    const convoId = conversation?.conversation_id || initialConversationId || '__pending__';
    const toastKey = `${convoId}:${opts.reason || 'admin'}`;
    if (modelSubstitutedToastedRef.current.has(toastKey)) return;
    modelSubstitutedToastedRef.current.add(toastKey);
    // The releases, by name: what answers and what was asked for — the
    // server names both for this conversation's tenant (utils/modelLabels).
    const names = substitutionNames(
      {
        effective_model: effectiveModel,
        requested_model: opts.requested,
        effective_model_name: opts.effectiveName,
        requested_model_name: opts.requestedName,
      },
      allModels,
      previousModel,
    );
    const previousName = (previousModel || opts.requestedName) ? names.previous : '';
    const effectiveName = names.effective;
    if (opts.reason === 'provider_busy') {
      antdMessage.info({
        content: t('chat.modelBusyToast', {
          previous: previousName || effectiveName,
          effective: effectiveName,
          defaultValue: `${previousName} is busy right now; this reply came from ${effectiveName}.`,
        }),
        duration: 6,
      });
      return;
    }
    if (opts.reason === 'provider_unavailable') {
      antdMessage.warning({
        content: t('chat.modelUnavailableToast', {
          previous: previousName || effectiveName,
          effective: effectiveName,
          defaultValue: `${previousName} is not available for this agent anymore; it now uses ${effectiveName}.`,
        }),
        duration: 8,
      });
      return;
    }
    antdMessage.warning({
      content: previousName
        ? t('chat.modelSubstitutedToast', {
            previous: previousName,
            effective: effectiveName,
            defaultValue: `Your model (${previousName}) was disabled by an admin; using ${effectiveName} instead.`,
          })
        : t('chat.modelSubstitutedToastNoPrevious', {
            effective: effectiveName,
            defaultValue: `Your model was disabled by an admin; using ${effectiveName} instead.`,
          }),
      duration: 6,
    });
  };

  // Initialize chat based on props — uses ref object to prevent StrictMode double-loading
  useEffect(() => {
    const ctrl = { cancelled: false };

    // Race guard: when createConversationWithBot calls onNavigate to update the URL,
    // ChatPage's branches state lags one render behind the URL params. During that
    // window the panel re-renders with both initialConversationId and initialBotId
    // undefined — without this check, we'd fall into Case 3 and bounce the user
    // back to the agent picker, throwing away the conversation we just created.
    if (!initialConversationId && !initialBotId && conversation) {
      logger.debug('Skipping init: conversation already created (URL transition in progress)');
      return () => { ctrl.cancelled = true; };
    }

    // Reset error state on every effect re-run (id change or manual retry).
    setLoadFailed(false);
    setAgentUnavailable(false);
    // …and the model facts: this conversation's own model is loaded below.
    userPickedRef.current = false;
    setConversationModel(null);

    // If pre-loaded data is provided (branch panel), use it directly
    if (initialConversation && initialMessages) {
      logger.debug('Using pre-loaded data (branch panel)');
      setConversation(initialConversation);
      setMessages(initialMessages);
      setLoading(false);
      // Its own model is known now: never wait for the agent to show it.
      adoptConversationContext(initialConversation.conversation_context);
      // This branch skips the fetch below, and used to skip the artifact
      // hydration with it — so a conversation opened from pre-loaded data
      // showed no artifact panel even when it had artifacts.
      artifacts.reset();
      void artifacts.hydrateFromMessages(
        initialMessages, initialConversation?.conversation_id);
      void loadDriveEdits(initialConversation?.conversation_id);

      // Still load the bot if the conversation has one
      if (initialConversation.chatbot_id) {
        fetchBotShared(initialConversation.chatbot_id)
          .then((botData) => {
            if (!ctrl.cancelled) {
              setBot(botData);
              const modeConfig = (botData as any).mode_slider_config;
              if (modeConfig?.enabled) {
                const savedIdx = initialConversation.conversation_context?.active_mode_index;
                setActiveModeIndex(savedIdx ?? modeConfig.default_index ?? 0);
              }
            }
          })
          .catch((error) => {
            logger.debug('Bot not found or no access:', error);
            if (!ctrl.cancelled) setAgentUnavailable(true);
          });
      }

      // Auto-scroll to bottom after loading
      setTimeout(() => scrollToBottom(true), 100);
      return () => { ctrl.cancelled = true; };
    }

    // Otherwise, initialize from props
    initializeChat(ctrl);
    return () => { ctrl.cancelled = true; };
    // loadRetryNonce bumps trigger a re-run for the manual Retry button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConversationId, initialBotId, loadRetryNonce]);

  /**
   * Initialize chat based on props.
   * @param ctrl - cancellation ref shared with useEffect cleanup
   */
  const initializeChat = async (ctrl: { cancelled: boolean } = { cancelled: false }) => {
    // Skip if pre-loaded data was provided
    if (initialConversation && initialMessages) return;

    // A just-created conversation (createConversationWithBot below) is in
    // `_panelCache`, and this panel's `conversation` / `bot` state was already
    // seeded from it at mount — so there is nothing to spin for. The entry
    // is consumed here, once, and the server is STILL asked for the
    // transcript below: the cache pre-paints the header, it never stands in
    // for the messages.
    //
    // It used to `return` on a cache hit, and that is exactly how a recent
    // conversation opened EMPTY in production (2026-09-27): the creating
    // panel updates the URL with history.replaceState, which never remounts
    // it, so the entry outlived the conversation's first turns; the next
    // mount of that conversation — the person clicking it in the sidebar,
    // right away or after visiting another chat — took `conversation` from
    // the cache and skipped the load, leaving `messages` at its initial `[]`.
    // On dev builds React StrictMode's second effect pass found the entry
    // already consumed and loaded normally, which is why it never reproduced
    // locally. Pinned by tests/e2e/sidebar-recent-conversation-not-empty.spec.js.
    const cached = initialConversationId ? _panelCache.get(initialConversationId) : undefined;
    if (initialConversationId && cached) {
      logger.debug('Seeding from just-created conversation cache:', initialConversationId);
      _panelCache.delete(initialConversationId);
      // The agent came with the cache entry (the `bot` state was seeded from
      // it); its conversation's own model must come with it too.
      adoptConversationContext(cached.conversation?.conversation_context);
    } else {
      setLoading(true);
    }

    try {
      // Case 1: Load existing conversation
      if (initialConversationId) {
        // First load attempt
        let ok = await loadConversation(initialConversationId, ctrl);
        if (ctrl.cancelled) return;

        // Auto-retry once on transient failure (network flake, axios 30s
        // timeout, malformed response). The "blank page or endless spinner"
        // bug is almost always a transient flake — refreshing fixes it
        // because the second request usually succeeds.
        if (!ok) {
          logger.warn('Conversation load failed, auto-retrying once...');
          await new Promise<void>((resolve) => setTimeout(resolve, 800));
          if (ctrl.cancelled) return;
          ok = await loadConversation(initialConversationId, ctrl);
          if (ctrl.cancelled) return;
        }

        // If it still failed, surface the recoverable error panel rather
        // than redirecting silently to home. The user keeps the URL they
        // navigated to and can hit Retry without losing context.
        if (!ok) {
          setLoadFailed(true);
        }
      }
      // Case 2: Create new conversation with bot
      else if (initialBotId) {
        await createConversationWithBot(initialBotId);
      }
      // Case 3: neither a conversation id nor a bot id was provided. We
      // intentionally do NOT auto-create a "general" (no-bot) chat from this
      // path — that surface is not user-reachable by design. If we ever land
      // here, redirect to the home agent picker. (Education flows mount
      // ConversationPanel with their own initialConversationId, so this
      // doesn't affect that flow.)
      else {
        console.warn('[ConversationPanel] Mounted with neither initialConversationId nor initialBotId — redirecting to agent picker');
        if (_onNavigate) {
          _onNavigate('/');
        } else if (typeof window !== 'undefined') {
          // Fall back to a path-based redirect. Tries to keep the
          // user inside their current tenant by reading the URL slug;
          // otherwise the root route handles the auth-aware redirect.
          const slugMatch = window.location.pathname.match(/^\/([^/]+)(\/|$)/);
          const slug = slugMatch?.[1]?.toLowerCase();
          window.location.assign(slug && !isReservedSlug(slug) ? `/${slug}/` : '/');
        }
        return;
      }
    } catch (error) {
      console.error('Error initializing chat:', error);
      showNotification(t('chat.failedToInitialize'), 'error');
      if (!ctrl.cancelled && initialConversationId) {
        setLoadFailed(true);
      }
    } finally {
      // Always clear the spinner — even when StrictMode cancels the run,
      // the second invocation will set it back to true. Guarding on
      // !ctrl.cancelled here was the source of the "endless loading"
      // symptom: when the run was cancelled mid-flight, no later code
      // would ever flip loading off.
      if (!ctrl.cancelled) {
        setLoading(false);
      }
    }
  };

  /**
   * Load existing conversation. Returns true on success, false on a
   * recoverable failure (network error, malformed response). Never throws.
   * @param ctrl - cancellation ref shared with useEffect cleanup; prevents stale state updates in StrictMode
   */
  const loadConversation = async (conversationId: string, ctrl: { cancelled: boolean } = { cancelled: false }): Promise<boolean> => {
    // Clear any stale conversation-budget banner from a previous chat — the
    // banner is conversation-scoped (Change C). Without this, navigating from
    // a FULL conversation to a fresh one would keep the red banner showing.
    setConversationBanner(null);
    try {
      // Load raw response so we can read top-level fields like
      // `effective_model`, `model_substituted`, `no_available_models`.
      const [convResp, messagesData] = await Promise.all([
        api.get(`/conversations/${conversationId}`),
        conversationService.getMessages(conversationId),
      ]);
      const convPayload = convResp.data || {};
      const convData = convPayload.conversation;

      // Don't update state if the effect was cleaned up (StrictMode remount)
      if (ctrl.cancelled) return true;

      // Defense against malformed responses (gateway interjection, partial
      // proxy, backend bug): if the conversation envelope is missing,
      // surfacing it as a load failure triggers our retry path instead of
      // committing `null` to state and rendering a blank chat.
      if (!convData) {
        logger.warn('Conversation response missing data.conversation', { conversationId });
        return false;
      }

      // E1: surface "no models available" panel and bail out of model setup.
      if (convPayload.no_available_models) {
        setNoModelsAvailable(true);
        setConversation(convData);
        setMessages(messagesData);
        return true;
      }
      setNoModelsAvailable(false);

      setConversation(convData);
      setMessages(messagesData);

      // Clear artifacts from the previous conversation, then hydrate the panel
      // from this conversation's artifacts and open it automatically (no need to
      // click an artifact in the chat first).
      artifacts.reset();
      // Pass the conversation id too: attachments are the fast path, but an
      // artifact whose turn skipped the bind has none, and without this the
      // chat that produced it offers no way to reach it at all.
      void artifacts.hydrateFromMessages(messagesData, convData?.conversation_id);
      void loadDriveEdits(convData?.conversation_id);

      // Per-conversation branding overrides — always set (|| {}) so switching
      // conversations never leaks the previous one's opt-outs.
      setArtifactBranding(convData.conversation_context?.artifact_branding || {});

      // R2: if the backend tells us the bot's stored model isn't allowed
      // for this tenant, pre-set the picker to the substituted model
      // silently (no toast — the user didn't trigger anything yet).
      // The conversation's own model: what the server will run it on (its
      // effective model — substituted if the stored one is no longer
      // allowed), else its stored current model. The picker derives from it.
      setConversationModel(convPayload.effective_model || convData.conversation_context?.current_model || null);
      if (convPayload.context_usage) {
        setContextUsage(convPayload.context_usage as ContextUsage);
      }

      // Load existing feedback for this conversation (see messageRatingEnabled)
      if (messageRatingEnabled || skillFeedbackEnabled) {
        try {
          const fbResp = await api.get(`/conversations/${conversationId}/skill-feedback`);
          setSkillFeedbackMap(fbResp.data.feedback || {});
        } catch { setSkillFeedbackMap({}); }
      } else {
        setSkillFeedbackMap({});
      }

      // Load bot if conversation has one
      if (convData.chatbot_id) {
        try {
          const botData = await fetchBotShared(convData.chatbot_id);
          if (ctrl.cancelled) return true;
          setBot(botData);
          adoptConversationContext(convData.conversation_context);
          // Initialize mode from conversation context or bot default
          const modeConfig = (botData as any).mode_slider_config;
          if (modeConfig?.enabled) {
            const savedIdx = convData.conversation_context?.active_mode_index;
            setActiveModeIndex(savedIdx ?? modeConfig.default_index ?? 0);
          }
        } catch (error) {
          logger.debug('Bot not found or no access:', error);
          if (!ctrl.cancelled) setAgentUnavailable(true);
        }
      } else {
        setBot(null);
      }

      // Auto-scroll to bottom after loading existing conversation
      setTimeout(() => scrollToBottom(true), 100);
      return true;
    } catch (error) {
      console.error('Error loading conversation:', error);
      // Caller decides what to do: initializeChat retries once, then surfaces
      // the recoverable error panel. We don't redirect to home here — that
      // mid-load redirect was experienced by users as a "blank page" because
      // they expected to land on the conversation they clicked.
      return false;
    }
  };

  /**
   * Create new conversation with specific bot
   */
  const createConversationWithBot = async (botId: string) => {
    // Guard against React StrictMode double-invocation
    if (initializingBotIdRef.current === botId) {
      logger.debug('Already creating conversation for bot:', botId);
      return;
    }
    initializingBotIdRef.current = botId;

    try {
      // Force a fresh fetch — for new conversations we need the latest bot
      // config (specifically mode_slider_config.default_index) which may
      // have been edited in BotModal since the cache was populated. Without
      // this, the slider lands on the cached/old default until a page reload
      // wipes the cache.
      const botData = await fetchBotShared(botId, /* forceFresh */ true);
      logger.debug('Creating conversation with bot:', {
        id: botData.chatbot_id,
        handler_config: botData.handler_config,
        conversation_capabilities: botData.conversation_capabilities,
      });
      setBot(botData);
      setConversationModel(null);   // a new conversation: the agent's default
      // Sync slider to the bot's default mode for the new conversation
      const newConvModeConfig = (botData as any).mode_slider_config;
      if (newConvModeConfig?.enabled) {
        setActiveModeIndex(newConvModeConfig.default_index ?? 0);
      }

      // Create conversation
      // `projectId` (when present) attaches the thread to a shared project:
      // the server validates membership and stamps the PROJECT's tenant, which
      // is what makes it readable by members from other organizations.
      const convData = await conversationService.createConversation(botId, projectId, threadMode);
      setConversation(convData);
      setMessages([]);
      setConversationBanner(null);

      // Populate module-level cache so that if a sidebar click remounts this
      // conversation, the new instance paints its header from the cache with
      // no loading flash. It still fetches the transcript (see initializeChat).
      _panelCache.set(convData.conversation_id, { conversation: convData, bot: botData });
      // Evict oldest entries when cache exceeds max size
      if (_panelCache.size > MAX_PANEL_CACHE_SIZE) {
        const oldestKey = _panelCache.keys().next().value;
        if (oldestKey) _panelCache.delete(oldestKey);
      }

      // Update URL silently — skip if embedded (onNavigate provided) to avoid hijacking parent route
      if (_onNavigate) {
        _onNavigate(`/chat?conversation=${convData.conversation_id}`);
      } else if (!initialConversationId) {
        window.history.replaceState(null, '', `/#/chat?conversation=${convData.conversation_id}`);
      }

      logger.debug('Created conversation with bot:', botId);
    } catch (error) {
      console.error('Error creating conversation with bot:', error);
      showNotification(t('chat.failedToCreateConversation'), 'error');
      // The wait for the agent is over too (it only matters if the agent
      // itself never loaded — see `agentPending`).
      setAgentUnavailable(true);
      // Creation is definitively over — clear the marker NOW so a retry-send
      // hits the normal "No active conversation" path instead of being parked
      // into a ref nothing would ever flush (the delayed clear in `finally`
      // only exists to absorb StrictMode double-invocation of a SUCCESSFUL
      // creation).
      initializingBotIdRef.current = null;
      // Don't lose messages the user sent while creation was in flight —
      // ChatInput already cleared the textarea, so put the text back.
      const queued = pendingSendsRef.current;
      pendingSendsRef.current = [];
      const restoredText = queued.map((q) => q.content).filter(Boolean).join('\n');
      if (restoredText) {
        setEditInputValue(restoredText);
        setEditTimestamp(Date.now());
      }
      if (queued.some((q) => q.attachments?.length)) {
        // Files can't be restored into the input — say so instead of
        // silently dropping them alongside the restored text.
        showNotification(t('common.errors.reattachFiles', { defaultValue: 'Your attached files were not sent — please attach them again.' }), 'error');
      }
    } finally {
      setTimeout(() => {
        initializingBotIdRef.current = null;
      }, 1000);
    }
  };

  // A `createNewConversation()` helper used to live here that posted to the
  // conversations endpoint with no chatbot_id, producing a "general" (no-agent)
  // chat. That surface isn't part of the user-reachable product — every
  // legitimate entry point passes initialConversationId or initialBotId — so
  // the helper has been removed. To start a new chat, route through
  // MainPage's agent picker instead.

  /**
   * Extract provider and model from model string
   */
  const resolveSendModel = (modelName: string): { provider: string; model: string } => {
    logger.debug('resolveSendModel input:', modelName);

    // Catalog first: family keys ('sonnet', 'flash', …) carry their provider
    // in the /ai/models/ response, and legacy concrete ids resolve to their
    // family via resolved_model. The family key is what gets sent as the
    // model ref — the backend resolves it to the pinned concrete version.
    const entry = findModelEntry(allModels, modelName);
    if (entry?.provider) {
      logger.debug('resolveSendModel output (catalog):', { provider: entry.provider, model: entry.id });
      return { provider: entry.provider, model: entry.id };
    }

    // A ref missing from the catalog (e.g. models fetch still in flight) is
    // read by its release prefix — the composer's own rule
    // (utils/modelProvider). The backend settles the provider from the model
    // anyway (`_reconcile_provider_with_model`).
    let model: string = modelName;
    let provider = providerOfModel(allModels, modelName);
    if (!provider) {
      provider = 'openai';
      model = model || defaultModelRef.current;
    }

    logger.debug('resolveSendModel output:', { provider, model });
    return { provider, model };
  };

  /**
   * Handle edit message - ChatGPT style (edit & replace)
   */
  const handleEditMessage = (message: Message) => {
    setEditingMessage(message);
    setEditInputValue(message.content || '');
    setEditTimestamp(Date.now());
  };

  /**
   * Send message handler with streaming support
   */
  const handleSendMessage = async (content: string, attachments?: File[], driveFiles?: DriveFileRef[]): Promise<SendOutcome> => {
    if (!conversation) {
      // Bot-card race: the user can type and hit send before
      // createConversationWithBot()'s POST resolves (ChatInput has already
      // cleared the textarea by now). Park the message; the effect below
      // sends it the moment the conversation lands.
      if (initializingBotIdRef.current) {
        pendingSendsRef.current.push({ content, attachments, driveFiles });
        return;
      }
      showNotification(t('chat.noActiveConversation'), 'error');
      return;
    }

    if (!content.trim() && !attachments?.length && !driveFiles?.length) {
      return;
    }

    // Single-flight: if a send is already streaming (the auto-send and the
    // parked-message flush can race onto the same commit), park this message
    // instead of running two concurrent sends through the shared
    // abortController/isStreaming/status state. The flush effect re-fires
    // when the active send completes and delivers it in order.
    if (sendInFlightRef.current) {
      pendingSendsRef.current.push({ content, attachments, driveFiles });
      return;
    }
    sendInFlightRef.current = true;

    // Fresh wiki-graph traversal for this turn (keeps the loaded graph + panel).
    if (wikiFeatureEnabled) wikiGraph.resetTurn();

    // Capture the edit before clearing state
    const editTarget = editingMessage;
    const editMessageId = editingMessage?.message_id || null;

    logger.debug('Sending message:', {
      conversationId: conversation.conversation_id,
      contentLength: content.length,
      model: selectedModel,
      hasAttachments: !!attachments?.length,
      isEditing: !!editingMessage,
      editMessageId
    });

    // Handle edit mode - truncate messages from the edited one onwards (UI only)
    if (editingMessage) {
      const editIndex = messages.findIndex(m => m.message_id === editingMessage.message_id);
      if (editIndex !== -1) {
        setMessages(prev => prev.slice(0, editIndex));
      }
      setEditingMessage(null);
      setEditInputValue('');
    }

    // Generate temp IDs
    const tempUserMsgId = `temp-user-${Date.now()}`;
    const tempAIMsgId = `temp-ai-${Date.now()}`;

    // Add optimistic user message. `render_key` stays the same when the
    // server returns the real `message_id` on `ai_complete` — that's what
    // keeps the bubble from unmounting/remounting at the end of streaming.
    const tempUserMessage: Message = {
      message_id: tempUserMsgId,
      render_key: tempUserMsgId,
      conversation_id: conversation.conversation_id,
      role: 'user',
      content: content.trim(),
      created_at: new Date().toISOString(),
      // Stamped locally so the author tag appears on the very first frame in a
      // project thread. The server sends the authoritative values back on the
      // `user_message` event.
      sender_user_id: signedInUser?.user_id,
      sender_name: signedInUser?.full_name
        || (signedInUser as any)?.user_full_name
        || signedInUser?.email,
      sender_email: signedInUser?.email,
      // Show the attachments optimistically so the user's message (and, for a
      // files-only send, its file card) is visible immediately during upload —
      // otherwise an empty-content, empty-attachment temp message is filtered
      // out until the server confirms it. Real ids arrive on the user_message
      // event and replace these.
      attachments: [
        ...(attachments ?? []).map((f, i) => ({
          attachment_id: `${tempUserMsgId}-att-${i}`,
          file_name: f.name,
          file_type: f.type || '',
          file_size: f.size,
        })),
        ...(driveFiles ?? []).map((d) => ({
          attachment_id: `${tempUserMsgId}-drive-${d.file_id}`,
          file_name: d.name,
          file_type: d.mime_type || '',
          file_size: d.size ?? 0,
          source: { provider: 'google_drive' as const, file_id: d.file_id, web_view_link: d.web_view_link },
        })),
      ]
    };

    // Add placeholder for AI message.
    // We populate `mode_label` and `model_name` on the placeholder so
    // MessageList's `modeSwitches` / `modelSwitches` dividers (which
    // derive from these fields) land at message creation, not on
    // `ai_complete`. Without this, the divider pops in at the end of
    // streaming and pushes the bubble down — the user-visible "jump".
    const _modeCfg = (bot as any)?.mode_slider_config;
    const _modeLabel: string | undefined =
      _modeCfg?.enabled && Array.isArray(_modeCfg?.modes) && _modeCfg.modes[activeModeIndex]?.label
        ? _modeCfg.modes[activeModeIndex].label
        : undefined;
    const tempAIMessage: Message = {
      message_id: tempAIMsgId,
      render_key: tempAIMsgId,
      conversation_id: conversation.conversation_id,
      role: 'assistant',
      content: '',
      created_at: new Date().toISOString(),
      attachments: [],
      model_name: selectedModel || undefined,
      mode_label: _modeLabel,
    };

    logger.debug('Adding temporary messages to UI');
    setMessages((prev) => [...prev, tempUserMessage, tempAIMessage]);
    setSending(true);
    setActiveSkills([]);
    activeSkillsRef.current = [];
    setDebugEvents([]);
    // Reset the live-status queue + state. For file uploads the backend
    // immediately overrides this with 'uploading' then 'opening' (and
    // 'preparing' for the RAG path); for text-only sends the default
    // 'thinking' is the right starting label.
    clearLiveStatus();
    statusShownAtRef.current = Date.now();
    const firstFileName = attachments?.[0]?.name ?? driveFiles?.[0]?.name;
    setThinkingPhase(firstFileName ? 'uploading' : 'thinking');
    setThinkingFilename(firstFileName);
    messageCountRef.current += 1;

    // Force scroll to bottom when sending new message
    scrollToBottom(true);

    // Capture selectedModel at call time to avoid stale closure issues
    const currentModel = selectedModel;

    try {
      if (attachments?.length || driveFiles?.length) {
        await handleSendWithFiles(content, attachments ?? [], driveFiles ?? [], tempUserMsgId, tempAIMsgId, currentModel, editMessageId);
      } else {
        await handleStreamingMessage(content, tempUserMsgId, tempAIMsgId, currentModel, editMessageId);
      }

      logger.debug('Message sent successfully');

    } catch (error) {
      console.error('[ConversationPanel] Send error:', error);

      // Standardized upload-error contract: render the friendly Modal
      // instead of a toast so the user sees the actual reason. Falls back
      // to the legacy toast for non-upload errors.
      const rendered = showUploadErrorModal(error, t);
      if (!rendered) {
        showNotification(
          error instanceof Error ? error.message : t('common.sendMessageFailed'),
          'error'
        );
      }

      // Remove temporary messages on error
      setMessages((prev) => prev.filter(m =>
        m.message_id !== tempUserMsgId && m.message_id !== tempAIMsgId
      ));

      // Refused before the stream opened — a refused file (UploadError), or
      // any HTTP refusal (ApiError) — the server never took the message. The
      // composer gives the words back; an edit goes back to being an edit,
      // with the messages it hid.
      if (error instanceof UploadError || error instanceof ApiError) {
        if (editTarget) {
          setEditingMessage(editTarget);
          void syncMessages();
        }
        return 'not_taken';
      }
    } finally {
      sendInFlightRef.current = false;
      setSending(false);
    }
  };

  // Keep the ref updated with the latest handleSendMessage function
  useEffect(() => {
    sendMessageRef.current = handleSendMessage;
  });

  // Flush a message parked while createConversationWithBot() was in flight or
  // while another send was streaming (see handleSendMessage's park branches).
  // Ordering rules: the auto-send initialMessage (typed FIRST, on the home
  // page) goes before a parked message, and nothing flushes while a send is
  // in flight — the `sending`/`isStreaming` deps re-fire this effect when the
  // active send completes.
  useEffect(() => {
    if (!conversation || pendingSendsRef.current.length === 0) return;
    if (sending || isStreaming || sendInFlightRef.current) return;
    if (initialMessage && !hasAutoSentRef.current) return; // auto-send goes first
    const queued = pendingSendsRef.current.shift()!;
    void (async () => {
      await Promise.resolve(); // let sendMessageRef pick up the latest closure
      await sendMessageRef.current?.(queued.content, queued.attachments, queued.driveFiles);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.conversation_id, sending, isStreaming, initialMessage]);

  // Auto-send once the conversation is ready.
  // Prefers the `autoSendMessage` prop (direct handoff from MainPage — survives
  // StrictMode remounts); falls back to `initialMessage` state (sessionStorage
  // path used by URL-based entries). One-shot, guarded by hasAutoSentRef.
  //
  // Design notes:
  //   1. The guard (hasAutoSentRef) is checked first, so once we've committed
  //      to sending, re-runs of this effect bail immediately.
  //   2. We DON'T clear initialMessage / pendingMessageRef inside this effect.
  //      Doing so would trigger a dep change, which would tear down a pending
  //      setTimeout via cleanup and drop the message. The guard alone is
  //      sufficient to prevent a double-send.
  //   3. The send runs in an async IIFE (no setTimeout) so it can't be canceled
  //      by the effect cleanup — the message always reaches the server.
  useEffect(() => {
    if (hasAutoSentRef.current) return;
    if (!conversation?.conversation_id) return;
    if (!selectedModel) return;
    if (loading || sending) return;

    const messageToSend = (autoSendMessage || initialMessage || '').trim();
    if (!messageToSend) return;

    logger.debug('Conditions met for auto-send:', {
      via: autoSendMessage ? 'prop' : 'initialMessage',
      messageToSend,
      conversationId: conversation?.conversation_id,
      selectedModel,
    });

    hasAutoSentRef.current = true;

    // URL update — safe, doesn't touch React state the effect depends on.
    if (conversation) {
      if (_onNavigate) {
        _onNavigate(`/chat?conversation=${conversation.conversation_id}`);
      } else if (!initialConversationId) {
        window.history.replaceState(null, '', `/#/chat?conversation=${conversation.conversation_id}`);
      }
    }
    // Clear the legacy sessionStorage handoff so a reload doesn't re-send.
    sessionStorage.removeItem('pendingChatMessage');
    sessionStorage.removeItem('pendingChatModel');
    // Empty the ChatInput so the user sees a fresh input after the auto-send.
    // Safe to do AFTER setting hasAutoSentRef.current=true — the re-run that
    // this setState triggers will bail on the guard.
    setInitialMessage('');
    pendingMessageRef.current = null;

    (async () => {
      // One microtask delay so sendMessageRef (updated in a separate every-render
      // effect) definitely reflects the latest handleSendMessage closure.
      await Promise.resolve();
      logger.debug('Auto-sending message now:', messageToSend);
      if (!sendMessageRef.current) {
        console.warn('[ConversationPanel] sendMessageRef not ready at auto-send time');
        hasAutoSentRef.current = false;
        return;
      }
      try {
        await sendMessageRef.current(messageToSend);
      } catch (error) {
        console.error('[ConversationPanel] Auto-send failed:', error);
        hasAutoSentRef.current = false;
      }
    })();
  }, [conversation?.conversation_id, selectedModel, loading, sending, initialMessage, autoSendMessage]);

  /**
   * Handle streaming message (no files)
   */
  /**
   * Shared reducer for the stream-event branches that are identical between
   * the text and with-files loops — including ALL live-progress events.
   * Returns true when the event was fully handled (caller `continue`s);
   * false for path-specific events (user_message, ai_chunk, ai_complete,
   * done, error) that stay in each loop.
   *
   * Status strings are NEVER written into message.content here (the old
   * behavior — the first token overwrote them forever); they flow through
   * the latest-wins status queue instead, so the status line survives token
   * arrival and multi-tool rounds.
   */
  const handleCommonStreamEvent = (
    event: StreamEvent,
    ctx: { tempAIMsgId: string; hasAiContent: () => boolean }
  ): boolean => {
    switch (event.type) {
      case 'thinking': {
        logger.debug('AI is thinking...');
        // Capture phase + filename for the ChatGPT-style indicator.
        if (
          event.phase === 'uploading' ||
          event.phase === 'opening' ||
          event.phase === 'preparing' ||
          event.phase === 'working' ||
          event.phase === 'thinking'
        ) {
          enqueueLiveStatus({ kind: 'phase', phase: event.phase, filename: event.filename });
        } else if (event.filename) {
          setThinkingFilename(event.filename);
        }
        // Keep the '...' placeholder only while no text has streamed —
        // MessageItem treats it as empty and shows the TypingIndicator.
        if (!ctx.hasAiContent()) {
          setMessages(prev => prev.map(m =>
            m.message_id === ctx.tempAIMsgId ? { ...m, content: '...' } : m
          ));
        }
        return true;
      }
      case 'processing':
        // Legacy file-indexing status (backend sends a freeform English
        // sentence). Map to the translated key so Hebrew isn't shown raw
        // English — the per-file `progress` events already carry the detail.
        logger.debug('Processing files:', event.status);
        enqueueLiveStatus({ kind: 'progress', key: 'indexingFiles' });
        return true;
      case 'web_search':
        logger.debug('AI is searching the web...');
        enqueueLiveStatus({ kind: 'progress', key: 'searchingWeb' });
        return true;
      case 'tool_call':
        logger.debug('AI is using tool:', event.tool_name);
        enqueueLiveStatus({ kind: 'progress', key: 'usingTool', tool: event.tool_name });
        return true;
      case 'progress':
        // Live backend progress (tool start/end, intra-tool steps, setup
        // phases). key → chat.progress.<key>; unknown keys degrade to the
        // generic fallback inside resolveLiveStatusLabel.
        enqueueLiveStatus({
          kind: 'progress',
          key: event.key,
          tool: event.tool,
          params: event.detail as Record<string, unknown> | undefined,
        });
        // Per-page wiki progress ALSO drives the live wiki graph, so the
        // trail lights up page by page as the agent reads — not in one
        // batch at call start (see the tool_debug branch below).
        if (
          wikiFeatureEnabled &&
          event.key === 'readingWikiPage' &&
          typeof event.detail?.page === 'string' &&
          event.detail.page
        ) {
          wikiGraph.onWikiTool('wiki_read_page', { page: event.detail.page });
        }
        return true;
      case 'thinking_step':
        // Live steps for the ThinkingBanner (reveal_thinking bots). Capped
        // defensively; persisted message.thinking_steps take over on reload.
        setThinkingSteps(prev =>
          prev.length >= 200
            ? prev
            : [...prev, {
                step_type: event.step_type as ThinkingStep['step_type'],
                label: event.label,
                detail: event.detail,
                timestamp: event.timestamp,
              }]
        );
        return true;
      case 'skill_activation':
        logger.debug('Skill activated:', event.skill_name);
        setActiveSkills(prev => {
          const updated = prev.includes(event.skill_name) ? prev : [...prev, event.skill_name];
          activeSkillsRef.current = updated;
          return updated;
        });
        // Track in dev tools — accumulates across conversation, never resets
        setActiveSkillEntries(prev => {
          if (prev.some(e => e.name === event.skill_name)) return prev;
          return [...prev, { name: event.skill_name, activatedAt: Date.now(), messageIndex: messageCountRef.current }];
        });
        return true;
      case 'skill_deactivation':
        logger.debug('Skill deactivated:', event.skill_name);
        // Don't set toolCallSinceLastChunk — deactivation fires between text chunks
        return true;
      case 'tool_debug':
        // Don't set toolCallSinceLastChunk for debug/result events
        setDebugEvents(prev => [...prev, { type: 'tool_debug', action: event.action, tool: event.tool, args: event.args, preview: event.preview, author: event.author, invocation_id: event.invocation_id, timestamp: Date.now() }]);
        if (wikiFeatureEnabled && event.action === 'call') {
          // Batch reads (wiki_read_pages) are deliberately NOT fed to the
          // graph here — lighting every page at call start would make the
          // whole trail appear at once. The per-page `progress` events light
          // them one by one as the agent actually reads (see 'progress').
          if (event.tool !== 'wiki_read_pages') {
            wikiGraph.onWikiTool(event.tool, event.args as any, (event as any).wiki_pages);
          }
          // Agent actually OPENED a wiki page → surface the Wiki tab and
          // expand the rail (discovery-only tools like wiki_search/grep are
          // skipped, mirroring the hook's own don't-pop-an-empty-panel rule).
          if (typeof event.tool === 'string' && event.tool.startsWith('wiki_read')) {
            setSideView('wiki');
            setSideCollapsed(false);
          }
        }
        return true;
      case 'artifact_start':
      case 'artifact_render':
      case 'artifact_error':
      case 'artifact_show':
        // Live agent output artifact — opens the right-side panel and swaps
        // it from "generating" to the rendered preview. `artifact_show` is the
        // same panel opening on a file that already existed.
        handleArtifactEvent(event as any);
        return true;
      case 'drive_edit': {
        // The agent changed a Google Drive file: show it live + list the step.
        const edit = (event as any).edit as DriveEdit | undefined;
        if (edit?.edit_id) {
          setDriveEdits((prev) => prev.some((e) => e.edit_id === edit.edit_id) ? prev : [...prev, edit]);
          setDriveActiveFileId(edit.file_id);
          setSideView('drive');
          setSideCollapsed(false);
        }
        return true;
      }
      case 'google_access_needed':
      case 'google_access_resolved':
        // A Drive tool waits for more Google access; Allow / Not now above the input.
        return turnAccess.onStreamEvent(event);
      case 'connector_access_needed':
      case 'connector_access_resolved':
        // A connector tool waits for the sender's own account; Connect / Not now above the input.
        return turnConnector.onStreamEvent(event as any);
      case 'drive_show': {
        // The agent opened a Drive file for the user to look at. Same panel,
        // live file, but it is not a step: nothing changed and there is
        // nothing to undo, so it only gets a tab.
        const file = (event as any).file as DriveEdit | undefined;
        if (file?.file_id) {
          setDriveEdits((prev) => prev.some((e) => e.file_id === file.file_id)
            ? prev
            : [...prev, {
                ...file,
                edit_id: `shown-${file.file_id}`,
                tool: 'show_google_drive_file',
                summary: [],
                at: new Date().toISOString(),
                reverted: false,
                shown: true,
              }]);
          setDriveActiveFileId(file.file_id);
          setSideView('drive');
          setSideCollapsed(false);
        }
        return true;
      }
      case 'artifact_progress':
        // Artifact render-stage narration for the chat status line
        // ("Rendering the final document…" → "Uploading…" → "Saving…").
        // The panel itself keeps its own generating spinner — these events
        // exist so the CHAT always explains what the turn is doing.
        enqueueLiveStatus({
          kind: 'progress',
          key: `artifact_${event.stage}`,
          params: { title: event.title || t('chat.artifacts.untitled', { defaultValue: 'Untitled' }) },
        });
        return true;
      case 'context_usage':
        // The meter, live: turn start, after each tool round, after a
        // compaction, on complete.
        setContextUsage(event as ContextUsage);
        return true;
      case 'compacted':
        // The conversation was compacted before (or in the middle of) this
        // turn: draw the marker at the cut and clear the "full" banner — the
        // turn is going ahead.
        insertCompactionMarker(event.summary_message, event.folded_message_ids || []);
        setConversationBanner((prev) => (prev?.kind === 'full' ? null : prev));
        return true;
      case 'compaction_failed':
        logger.warn('Compaction failed, turn continues', event.reason);
        antdMessage.warning({
          content: t('chat.context.compactFailed', {
            defaultValue: "We couldn't compact this conversation right now. Try again in a moment.",
          }),
          duration: 5,
        });
        return true;
      case 'warning':
        // Conversation token-budget guard (Change C): the WARN-band soft
        // banner renders persistently at the top of the chat with the
        // exact percent. Other warnings keep the legacy toast UX.
        if (event.warning_code === 'CONVERSATION_NEARLY_FULL') {
          const percent =
            event.details?.percent ??
            Math.round(((event.details?.used ?? 0) / (event.details?.context_window ?? 1)) * 100);
          setConversationBanner({
            kind: 'warning',
            message: t('chat.banners.CONVERSATION_NEARLY_FULL', {
              percent,
              defaultValue: event.message,
            }),
          });
        } else {
          antdMessage.warning({
            content: event.message,
            duration: 5,
          });
        }
        return true;
      case 'model_substituted':
        // Server-side silent substitution (R3 / E4): the model the user
        // sent with was disallowed for the tenant or bot. Update the
        // picker, refresh /ai/models/, and toast once per session per
        // conversation.
        handleModelSubstituted(event.effective_model, {
          sticky: event.sticky,
          reason: event.reason,
          requested: event.requested_model,
          effectiveName: event.effective_model_name,
          requestedName: event.requested_model_name,
        });
        return true;
      case 'phase_advance':
        // Education flow: phase completed, dispatch to useFlowProgress hook
        window.dispatchEvent(new CustomEvent('flowPhaseAdvanced', { detail: event }));
        return true;
      default:
        return false;
    }
  };

  const handleStreamingMessage = async (
    content: string,
    tempUserMsgId: string,
    tempAIMsgId: string,
    currentModel: string,
    editMessageId: string | null = null
  ) => {
    logger.debug('Starting streaming with model:', currentModel, editMessageId ? `(editing ${editMessageId})` : '');

    let aiContent = '';
    let userMessageConfirmed = false;
    const { provider, model } = resolveSendModel(currentModel);
    startStreamAnim(tempAIMsgId);
    setThinkingSteps([]);           // live ThinkingBanner steps reset per turn
    statusShownAtRef.current = 0;   // first status of the turn shows instantly

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsStreaming(true);
    startIdleWatchdog();

    const cleanup = () => {
      logger.debug('Cleaning up stream');
      flushStreamAnim();
      stopStreamAnim();
      stopIdleWatchdog();
      setIsStreaming(false);
      clearLiveStatus();  // never leave a stuck status line
      void artifacts.reconcileGenerating();  // never leave a panel stuck "generating"
      setSending(false);
      abortControllerRef.current = null;
    };

    try {
      const stream = conversationService.streamMessage(
        conversation!.conversation_id,
        {
          content,
          provider,
          model,
          chatbot_id: conversation!.chatbot_id || undefined,
          web_search: bot?.enable_web_search || false,
          edit_message_id: editMessageId || undefined
        }
      );

      for await (const event of stream) {
        noteStreamSignal();
        if (abortController.signal.aborted) {
          logger.debug('Streaming aborted');
          break;
        }

        logger.debug('Stream event:', event.type);

        // Shared branches (status line, skills, artifacts, warnings, ...).
        if (handleCommonStreamEvent(event, { tempAIMsgId, hasAiContent: () => !!aiContent })) {
          continue;
        }

        if (event.type === 'user_message') {
          userMessageConfirmed = true;
          const realUserMsg: Message = {
            message_id: event.message.message_id,
            // Keep the placeholder's React identity so the confirmed bubble
            // doesn't unmount and replay its reveal animation.
            render_key: tempUserMsgId,
            conversation_id: event.message.conversation_id,
            role: event.message.sender_type,
            content: event.message.message_content,
            model_name: event.message.message_metadata?.model,
            mode_label: event.message.message_metadata?.mode_label,
            // Authorship must survive this rebuild. This explicit field list
            // was silently dropping it, which wiped the optimistic author tag
            // the instant the server confirmed the message — so in a shared
            // project your own message was never labelled while you watched.
            sender_user_id: event.message.sender_user_id ?? undefined,
            sender_name: event.message.sender_name ?? undefined,
            sender_email: event.message.sender_email ?? undefined,
            sender_tenant_name: event.message.sender_tenant_name ?? undefined,
            created_at: event.message.created_at,
            attachments: event.message.attachments || []
          };

          setMessages(prev => prev.map(m =>
            m.message_id === tempUserMsgId ? realUserMsg : m
          ));
        }
        else if (event.type === 'ai_chunk' || event.type === 'content') {
          aiContent += event.content;
          pushStreamText(aiContent);
          // Answer text is flowing — no status sentence may linger beside it.
          clearLiveStatusOnText();
        }
        else if (event.type === 'ai_complete') {
          // Flush remaining animated text, then stop animation
          flushStreamAnim();
          stopStreamAnim();
          clearLiveStatus();

          const realAIMsgId = event.message?.message_id;
          const frontendSkills = [...activeSkillsRef.current];
          const backendSkills: string[] = event.message?.message_metadata?.active_skills || [];
          const completedSkills = [...new Set([...frontendSkills, ...backendSkills])];

          setMessages(prev => prev.map(m =>
            m.message_id === tempAIMsgId
              ? {
                  ...m,
                  message_id: realAIMsgId || m.message_id,
                  // The stored answer, not the streamed one: the server may
                  // finish it (a quotation restored to the source's exact
                  // text), and a reload must not show different words.
                  content: event.message?.message_content ?? aiContent,
                  model_name: event.message?.message_metadata?.model,
                  answered_by: answeredByOf(event.message),
                  served_by_fallback: event.message?.message_metadata?.served_by_fallback === true,
                  mode_label: event.message?.message_metadata?.mode_label,
                  answer_cut_off: event.message?.message_metadata?.answer_cut_off === true,
                  answer_cut_off_reason: event.message?.message_metadata?.answer_cut_off_reason,
                  web_sources: event.message?.message_metadata?.web_sources,
                  personal_connectors_used: event.message?.message_metadata?.personal_connectors_used,
                  active_skills: completedSkills.length > 0 ? completedSkills : undefined,
                  // The turn's files. The server binds artifacts to this
                  // message before it sends this event, so they arrive here —
                  // and dropping them was why a deck you just watched being
                  // built showed no card until you left the chat and came
                  // back. Keep whatever the placeholder already carried (a
                  // user upload echoed onto the bubble) when none came.
                  attachments: event.message?.attachments?.length
                    ? event.message.attachments
                    : m.attachments,
                }
              : m
          ));

          // Handle title update
          if (event.title_update) {
            const newTitle = event.title_update!.conversation_title;
            const conversationId = event.title_update!.conversation_id;
            setConversation(prev => prev ? {
              ...prev,
              conversation_title: newTitle
            } : null);

            window.dispatchEvent(new CustomEvent('conversationTitleUpdated', {
              detail: { conversationId, title: newTitle, chatbotName: bot?.chatbot_name, isInternal: bot?.is_internal || false }
            }));
          }

          // Notify sidebar that conversation had activity (to move it to top).
          // IMPORTANT: include the full conversation shape — especially
          // parent_conversation_id and the avatar fields — so that if the
          // sidebar adds this entry (because a concurrent re-fetch hasn't
          // landed yet), branches stay grouped under their parent instead of
          // surfacing as a duplicate top-level item.
          if (conversation?.conversation_id) {
            logger.debug('AI complete - dispatching conversationActivity for:', conversation.conversation_id);
            window.dispatchEvent(new CustomEvent('conversationActivity', {
              detail: {
                conversationId: conversation.conversation_id,
                conversationData: {
                  ...conversation,
                  chatbot_name: bot?.chatbot_name ?? (conversation as any).chatbot_name,
                  avatar_color: bot?.avatar_color ?? (conversation as any).avatar_color,
                  avatar_variant: bot?.avatar_variant ?? (conversation as any).avatar_variant,
                  is_internal: bot?.is_internal || false,
                  last_message_at: new Date().toISOString(),
                }
              }
            }));
          }
        }
        else if (event.type === 'done') {
          logger.debug('Stream done');
          setActiveSkills([]);
          activeSkillsRef.current = [];
          clearLiveStatus();
        }
        else if (event.type === 'error') {
          clearLiveStatus();
          // Conversation hard-block from the budget guard (Change C) lands
          // here. Surface as a persistent blocking banner rather than a
          // throw — the user keeps the conversation history visible and
          // can pick a CTA. Other errors keep the existing throw path.
          if (event.error_code === 'CONVERSATION_FULL') {
            setConversationBanner({
              kind: 'full',
              message: t('chat.banners.CONVERSATION_FULL', {
                defaultValue: event.message,
              }),
            });
            cleanup();  // matches the with-files loop — stops streaming state + watchdog
            return; // stop stream cleanly
          }
          if (event.error_code === 'AUTO_COMPACT_FAILED') {
            // Auto-compact tried and failed — show the friendly modal
            // (same shape as upload errors) so the user knows to retry
            // or start a new chat.
            antdMessage.warning({
              content: t('chat.banners.AUTO_COMPACT_FAILED', {
                defaultValue: event.message,
              }),
              duration: 6,
            });
            cleanup();  // matches the with-files loop — stops streaming state + watchdog
            return;
          }
          // A coded error is shown in the user's language (chat.errors.*);
          // the server's text is the fallback for codes this build lacks.
          throw new Error(event.error_code
            ? t(`chat.errors.${event.error_code}`, { defaultValue: event.error })
            : event.error);
        }
      }

      if (!userMessageConfirmed && !abortController.signal.aborted) {
        console.warn('[ConversationPanel] User message not confirmed, syncing...');
        await syncMessages();
      }

      cleanup();

    } catch (error) {
      console.error('[ConversationPanel] Streaming error:', error);
      cleanup();
      throw error;
    }
  };

  /**
   * Continue an answer the output cap paused — Claude.ai's "Continue". The
   * SAME message grows: what the model writes types out after the text it
   * already has, and the stored answer replaces it on completion (paused
   * again if the cap cut it again — the notice and button come back). No user
   * message is added. Stop works as for any turn.
   */
  const handleContinue = async (paused: Message) => {
    // The ref, not the state: it is set synchronously below, so a second click
    // in the same tick (a double click) returns here instead of sending a
    // second Continue (staging: two calls billed, one thrown away).
    if (!conversation?.conversation_id || continuingIdRef.current || isStreaming || sending) return;
    const id = paused.message_id;
    let piece = '';
    let completed = false;
    continuingIdRef.current = id;
    setSending(true);
    setMessages(prev => prev.map(m => m.message_id === id ? { ...m, answer_cut_off: false } : m));
    startStreamAnim(id, paused.content);
    setThinkingSteps([]);
    statusShownAtRef.current = 0;

    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    setIsStreaming(true);
    startIdleWatchdog();

    const cleanup = () => {
      flushStreamAnim();
      stopStreamAnim();
      stopIdleWatchdog();
      setIsStreaming(false);
      clearLiveStatus();
      void artifacts.reconcileGenerating();
      setSending(false);
      abortControllerRef.current = null;
      continuingIdRef.current = null;
    };

    try {
      for await (const event of conversationService.continueMessage(conversation.conversation_id, id)) {
        noteStreamSignal();
        if (abortController.signal.aborted) break;
        // The message already has text: a `thinking` event must not blank it.
        if (handleCommonStreamEvent(event, { tempAIMsgId: id, hasAiContent: () => true })) continue;
        if (event.type === 'ai_chunk' || event.type === 'content') {
          piece += event.content;
          pushStreamText(piece);
          clearLiveStatusOnText();
        } else if (event.type === 'ai_complete') {
          completed = true;
          flushStreamAnim();
          stopStreamAnim();
          clearLiveStatus();
          const stored = event.message;
          setMessages(prev => prev.map(m => m.message_id === id
            ? {
                ...m,
                content: stored?.message_content ?? (paused.content + piece),
                answer_cut_off: stored?.message_metadata?.answer_cut_off === true,
                answer_cut_off_reason: stored?.message_metadata?.answer_cut_off_reason,
                answer_limit_reached: stored?.message_metadata?.answer_limit_reached === true,
                web_sources: stored?.message_metadata?.web_sources ?? m.web_sources,
                personal_connectors_used: stored?.message_metadata?.personal_connectors_used ?? m.personal_connectors_used,
              }
            : m));
        } else if (event.type === 'done') {
          clearLiveStatus();
        } else if (event.type === 'error') {
          throw new Error(event.error_code
            ? t(`chat.errors.${event.error_code}`, { defaultValue: event.error })
            : event.error);
        }
      }
      const stopped = abortController.signal.aborted;
      cleanup();
      // Ended without its answer (stopped elsewhere — another tab, a durable
      // Stop): what the server kept is the truth.
      if (!completed && !stopped) await syncMessages();
    } catch (error) {
      console.error('[ConversationPanel] Continue error:', error);
      cleanup();
      showNotification(
        error instanceof ApiError && error.code
          ? t(`chat.errors.${error.code}`, { defaultValue: error.message })
          : error instanceof Error ? error.message : t('common.sendMessageFailed'),
        'error');
      // What the server kept is the truth (a failed Continue keeps what it
      // added, still paused).
      await syncMessages();
    }
  };

  /**
   * Handle sending message with files (with streaming)
   */
  const handleSendWithFiles = async (
    content: string,
    files: File[],
    driveFiles: DriveFileRef[],
    tempUserMsgId: string,
    tempAIMsgId: string,
    currentModel: string,
    editMessageId: string | null = null
  ) => {
    logger.debug('Uploading files with streaming:', files.length, 'model:', currentModel, editMessageId ? `(editing ${editMessageId})` : '');

    const { provider, model } = resolveSendModel(currentModel);

    let aiContent = '';
    let userMessageConfirmed = false;
    startStreamAnim(tempAIMsgId);
    setThinkingSteps([]);           // live ThinkingBanner steps reset per turn
    statusShownAtRef.current = 0;   // first status of the turn shows instantly

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsStreaming(true);
    startIdleWatchdog();

    const cleanup = () => {
      logger.debug('Cleaning up file stream');
      flushStreamAnim();
      stopStreamAnim();
      stopIdleWatchdog();
      setIsStreaming(false);
      clearLiveStatus();  // never leave a stuck status line
      void artifacts.reconcileGenerating();  // never leave a panel stuck "generating"
      setSending(false);
      abortControllerRef.current = null;
    };

    try {
      const stream = conversationService.streamMessageWithFiles(
        conversation!.conversation_id,
        {
          // Files-only send: keep the user message empty (no injected text) so
          // the bubble shows just the attachment. The attached file's content
          // is inlined into the user turn server-side, so the agent responds
          // to the file.
          content,
          files,
          drive_files: driveFiles.length ? driveFiles : undefined,
          provider,
          model,
          chatbot_id: conversation!.chatbot_id || undefined,
          web_search: bot?.enable_web_search || false,
          edit_message_id: editMessageId || undefined
        }
      );

      for await (const event of stream) {
        noteStreamSignal();
        if (abortController.signal.aborted) {
          logger.debug('File streaming aborted');
          break;
        }

        // Shared branches (status line, skills, artifacts, warnings, ...).
        if (handleCommonStreamEvent(event, { tempAIMsgId, hasAiContent: () => !!aiContent })) {
          continue;
        }

        if (event.type === 'user_message') {
          userMessageConfirmed = true;
          const realUserMsg: Message = {
            message_id: event.message.message_id,
            // Keep the placeholder's React identity so the confirmed bubble
            // doesn't unmount and replay its reveal animation.
            render_key: tempUserMsgId,
            conversation_id: event.message.conversation_id,
            role: event.message.sender_type,
            content: event.message.message_content,
            model_name: event.message.message_metadata?.model,
            mode_label: event.message.message_metadata?.mode_label,
            // Authorship must survive this rebuild. This explicit field list
            // was silently dropping it, which wiped the optimistic author tag
            // the instant the server confirmed the message — so in a shared
            // project your own message was never labelled while you watched.
            sender_user_id: event.message.sender_user_id ?? undefined,
            sender_name: event.message.sender_name ?? undefined,
            sender_email: event.message.sender_email ?? undefined,
            sender_tenant_name: event.message.sender_tenant_name ?? undefined,
            created_at: event.message.created_at,
            attachments: event.message.attachments || []
          };

          setMessages(prev => prev.map(m =>
            m.message_id === tempUserMsgId ? realUserMsg : m
          ));

          if (event.conversation_title && conversation) {
            const newTitle = event.conversation_title!;
            setConversation(prev => prev ? {
              ...prev,
              conversation_title: newTitle
            } : prev);

            window.dispatchEvent(new CustomEvent('conversationTitleUpdated', {
              detail: { conversationId: conversation.conversation_id, title: newTitle, chatbotName: bot?.chatbot_name }
            }));
          }
        }
        else if (event.type === 'ai_chunk') {
          aiContent += event.content;
          pushStreamText(aiContent);
          // Answer text is flowing — no status sentence may linger beside it.
          clearLiveStatusOnText();
        }
        else if (event.type === 'ai_complete') {
          flushStreamAnim();
          stopStreamAnim();
          clearLiveStatus();

          if (event.message) {
            const frontendSkills = [...activeSkillsRef.current];
            const backendSkills: string[] = event.message.message_metadata?.active_skills || [];
            const completedSkills = [...new Set([...frontendSkills, ...backendSkills])];
            const realAIMsg: Message = {
              message_id: event.message.message_id,
              conversation_id: event.message.conversation_id,
              role: event.message.sender_type,
              content: event.message.message_content,
              model_name: event.message.message_metadata?.model,
              answered_by: answeredByOf(event.message),
              served_by_fallback: event.message.message_metadata?.served_by_fallback === true,
              answer_cut_off: event.message.message_metadata?.answer_cut_off === true,
              answer_cut_off_reason: event.message.message_metadata?.answer_cut_off_reason,
              web_sources: event.message.message_metadata?.web_sources,
              personal_connectors_used: event.message.message_metadata?.personal_connectors_used,
              active_skills: completedSkills.length > 0 ? completedSkills : undefined,
              created_at: event.message.created_at,
              attachments: event.message.attachments || []
            };

            setMessages(prev => prev.map(m =>
              m.message_id === tempAIMsgId ? realAIMsg : m
            ));
          }

          if (event.title_update && conversation) {
            const newTitle = event.title_update!.conversation_title;
            setConversation(prev => prev ? {
              ...prev,
              conversation_title: newTitle
            } : prev);

            window.dispatchEvent(new CustomEvent('conversationTitleUpdated', {
              detail: { conversationId: conversation.conversation_id, title: newTitle, chatbotName: bot?.chatbot_name }
            }));
          }

          if (conversation?.conversation_id) {
            logger.debug('AI complete (files) - dispatching conversationActivity for:', conversation.conversation_id);
            window.dispatchEvent(new CustomEvent('conversationActivity', {
              detail: {
                conversationId: conversation.conversation_id,
                conversationData: {
                  ...conversation,
                  chatbot_name: bot?.chatbot_name ?? (conversation as any).chatbot_name,
                  avatar_color: bot?.avatar_color ?? (conversation as any).avatar_color,
                  avatar_variant: bot?.avatar_variant ?? (conversation as any).avatar_variant,
                  is_internal: bot?.is_internal || false,
                  last_message_at: new Date().toISOString(),
                }
              }
            }));
          }
        }
        else if (event.type === 'done') {
          logger.debug('File stream done');
          setActiveSkills([]);
          activeSkillsRef.current = [];
          clearLiveStatus();
        }
        else if (event.type === 'error') {
          setActiveSkills([]);
          activeSkillsRef.current = [];
          clearLiveStatus();
          if (event.error_code === 'CONVERSATION_FULL') {
            setConversationBanner({
              kind: 'full',
              message: t('chat.banners.CONVERSATION_FULL', {
                defaultValue: event.message,
              }),
            });
            cleanup();
            return;
          }
          if (event.error_code === 'AUTO_COMPACT_FAILED') {
            antdMessage.warning({
              content: t('chat.banners.AUTO_COMPACT_FAILED', {
                defaultValue: event.message,
              }),
              duration: 6,
            });
            cleanup();
            return;
          }
          // A coded error is shown in the user's language (chat.errors.*);
          // the server's text is the fallback for codes this build lacks.
          throw new Error(event.error_code
            ? t(`chat.errors.${event.error_code}`, { defaultValue: event.error })
            : event.error);
        }
      }

      if (!userMessageConfirmed && !abortController.signal.aborted) {
        console.warn('[ConversationPanel] User message not confirmed, syncing...');
        await syncMessages();
      }

      cleanup();
      logger.debug('Files uploaded with streaming successfully');

    } catch (error) {
      console.error('[ConversationPanel] File streaming error:', error);
      cleanup();
      throw error;
    }
  };

  /**
   * Sync messages from backend (error recovery)
   */
  const syncMessages = async () => {
    if (!conversation?.conversation_id) return;

    try {
      logger.debug('Syncing messages from backend...');
      const updatedMessages = await conversationService.getMessages(
        conversation.conversation_id
      );

      setMessages(updatedMessages);
      logger.debug('Messages synced:', updatedMessages.length);
    } catch (error) {
      console.error('[ConversationPanel] Sync error:', error);
    }
  };

  /**
   * Stop generation handler
   */
  const handleStopGeneration = () => {
    logger.debug('Stop generation requested');
    if (abortControllerRef.current) {
      // Server-side cancel FIRST: turns are detached from the connection
      // now, so the abort below only stops delivery - this is what actually
      // stops the model / artifact work.
      // The screen freezes at what it shows at the click, like Claude.ai:
      // text received but not yet typed out is discarded (see stopFreeze.ts),
      // and the server keeps exactly what is shown.
      const anim = streamAnimRef.current;
      const frozen = anim ? frozenAtStop(anim) : null;
      const frozenId = anim?.msgId;
      const shownChars = frozen?.shownChars;
      const continuingId = continuingIdRef.current;
      const continuedFrom = continuingId ? anim?.base ?? null : null;
      // The row the server stores for this stop: read back below.
      const stoppedId = stoppedMessageId(messages, frozenId, continuingId);
      const stoppedTurn = stoppedTurnOf(messages, stoppedId, continuingId);
      // A Stop before the first word stores no row: nothing to read back.
      const shownText = (frozen && frozenId === stoppedId
        ? frozen.content : messages.find(m => m.message_id === stoppedId)?.content) ?? '';
      if (conversation?.conversation_id) {
        void conversationService.stopGeneration(conversation.conversation_id, shownChars);
      }
      abortControllerRef.current.abort();

      // The stream loop only notices the abort on its NEXT event, so every
      // background driver must stop HERE — otherwise the idle watchdog would
      // re-surface "Thinking…" beside the [STOPPED] message, and the
      // typewriter's next frame would overwrite the [STOPPED] tag.
      stopIdleWatchdog();
      stopStreamAnim();

      setMessages(prev => prev.map(m => {
        if (m.role === 'assistant' && (m.message_id.startsWith('temp-') || m.message_id === continuingId)) {
          const content = frozen && m.message_id === frozenId ? frozen.content : m.content;
          if (!content || content === '...' || content === '') {
            return { ...m, content: '[STOPPED]' };
          } else {
            return { ...m, content: content + '\n\n[STOPPED]' };
          }
        }
        return m;
      }));

      setIsStreaming(false);
      clearLiveStatus();  // never leave a stuck status line
      void artifacts.reconcileGenerating();  // never leave a panel stuck "generating"
      setSending(false);
      abortControllerRef.current = null;

      // …and [STOPPED] is a GUESS. Aborting the fetch is not the same thing
      // as the server having been cut off: it commits the assistant row as
      // soon as the model finishes and only then runs summary + title
      // generation (two LLM calls, seconds) before the final `complete`
      // event. A Stop anywhere in that gap tags a turn that actually
      // FINISHED. Settle it against stored truth, shortly — same reconcile
      // the AoA panel uses, same pure decision. A genuine stop is settled
      // too: its stored row carries what the stream never delivered — the
      // Sources and the release that served it — and they show under the
      // frozen text at once, not after a reload. Every inconclusive outcome
      // leaves the message exactly as it is.
      if (stopReconcileTimerRef.current !== null) {
        window.clearTimeout(stopReconcileTimerRef.current);
      }
      const seq = ++stopReconcileSeqRef.current;
      const stoppedConversationId = conversation?.conversation_id;
      stopReconcileTimerRef.current = window.setTimeout(() => {
        stopReconcileTimerRef.current = null;
        void (async () => {
          if (!stoppedConversationId || !stoppedId || !shownText.trim()) return;
          const verdict = await settleStoppedTurn(
            () => conversationService.getMessages(stoppedConversationId),
            (persisted) => {
              const v = reconcileStoppedTurn(persisted, stoppedTurn);
              // A stopped Continue whose answer still reads as before it
              // began has simply not been saved yet — no evidence of anything.
              return v.kind === 'completed' && continuedFrom !== null && v.content === continuedFrom
                ? { kind: 'unknown' as const } : v;
            },
            () => stopReconcileSeqRef.current !== seq,
          );
          if (verdict.kind === 'unknown') return;
          const stored = verdict.message;
          setMessages(prev => (
            // A row already on screen is an earlier turn's, never this stop's.
            stored.message_id !== stoppedId && prev.some(m => m.message_id === stored.message_id)
              ? prev
              : prev.map(m => {
                if (m.message_id !== stoppedId) return m;
                // Completed: the stored message replaces the tagged one, with
                // its real message_id, so copy / branch / regenerate work on
                // that turn like any other. Stopped: the frozen text stays.
                return verdict.kind === 'completed' ? stored : withStoredStop(m, stored);
              })
          ));
        })();
      }, STOP_RECONCILE_DELAY_MS);
    }
  };

  /**
   * Handle branch from message - shows popover first, API call happens after user confirms
   */
  const handleBranch = (message: Message, clickEvent?: { x: number; y: number }) => {
    if (!conversation || !onBranchCreated) return;

    // Dispatch event to ChatPage to show the branch mode popover
    // The actual API call happens when the user selects an option
    window.dispatchEvent(new CustomEvent('branchRequested', {
      detail: {
        conversationId: conversation.conversation_id,
        messageId: message.message_id,
        sourcePanel: panelId,
        clickX: clickEvent?.x,
        clickY: clickEvent?.y,
      }
    }));
  };

  /**
   * Open the agent chooser. The handoff itself is deferred until an agent is
   * picked — same shape as handleBranch, where the click only opens the UI and
   * ChatPage owns the API call and the resulting panel.
   */
  const handleBranchToAgent = (message: Message) => {
    if (!conversation) return;
    setAgentPickerMessage(message);
  };

  /**
   * Commit the handoff: tell ChatPage which message and which agent. ChatPage
   * creates the branch and mounts it, because only it knows the panel rail.
   */
  const handleAgentPicked = (bot: Bot, mode: HandoffOpenMode) => {
    const message = agentPickerMessage;
    setAgentPickerMessage(null);
    if (!conversation || !message) return;

    window.dispatchEvent(new CustomEvent('agentBranchRequested', {
      detail: {
        conversationId: conversation.conversation_id,
        messageId: message.message_id,
        targetChatbotId: bot.chatbot_id,
        targetChatbotName: bot.chatbot_name,
        openMode: mode,
        sourcePanel: panelId,
      }
    }));
  };

  /**
   * Teleport across a handoff card. Routed through ChatPage so the destination
   * can open as a panel in the rail (and so a chat that's ALREADY on screen is
   * focused rather than opened a second time).
   */
  const handleHandoffTeleport = (conversationId: string, messageId?: string) => {
    window.dispatchEvent(new CustomEvent('handoffTeleport', {
      detail: { conversationId, messageId, sourcePanel: panelId }
    }));
  };

  /**
   * Open the other side of a handoff BESIDE this pane rather than navigating.
   * Reuses the same event the sidebar's "Split screen" fires, so both entry
   * points share one implementation (and one panel cap) in ChatPage.
   */
  const handleHandoffSplit = (conversationId: string) => {
    window.dispatchEvent(new CustomEvent('openSplitScreen', {
      detail: { conversationId }
    }));
  };

  /**
   * Cancel edit mode handler
   */
  const handleCancelEdit = () => {
    setEditingMessage(null);
    setEditInputValue('');
  };

  /**
   * Model change handler
   */
  const handleModelChange = (model: string) => {
    userPickedRef.current = true;
    setSelectedModel(model);
    logger.debug('Model changed to:', model);
  };

  /**
   * Artifact branding toggle — optimistic local flip, persisted per
   * conversation so the agent's next tool binding sees it. On is the default,
   * so opting back in DELETES the key rather than storing a redundant true.
   */
  const handleArtifactBrandingToggle = useCallback((format: 'pptx' | 'docx', branded: boolean) => {
    setArtifactBranding(prev => {
      const next = { ...prev };
      if (branded) delete next[format]; else next[format] = false;
      return next;
    });
    const convId = conversation?.conversation_id;
    if (!convId) return;
    api.patch(`/conversations/${convId}/artifact-branding`, { format, branded })
      .catch(() => {
        // Roll back so the switch never lies about what the server will do.
        setArtifactBranding(prev => {
          const next = { ...prev };
          if (branded) next[format] = false; else delete next[format];
          return next;
        });
        antdMessage.error(t('chat.artifactBranding.saveFailed') || 'Could not save branding setting');
      });
  }, [conversation?.conversation_id, t]);

  /** Brandable formats this agent has switched on, in display order. */
  const artifactFormats = useMemo(() => {
    const enabled = (bot as any)?.handler_config?.output_formats || {};
    return (['pptx', 'docx'] as const).filter(f => !!enabled[f]);
  }, [bot]);

  /**
   * Scroll to bottom helper - uses ref instead of document.querySelector
   */
  const scrollToBottom = (force = false) => {
    setTimeout(() => {
      const container = messagesContainerRef.current;
      if (!container) return;

      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 200;

      if (force || isNearBottom) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 150);
  };

  /**
   * Scroll to bottom when messages change (but only if user was near bottom)
   */
  useEffect(() => {
    scrollToBottom(false);
  }, [messages]);

  // Show scroll button based on scroll direction, auto-hide after 5s
  const handleMessagesScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const scrollTop = container.scrollTop;
    const distFromBottom = container.scrollHeight - scrollTop - container.clientHeight;
    const scrollingUp = scrollTop < lastScrollTopRef.current;
    lastScrollTopRef.current = scrollTop;

    const nearTop = scrollTop < 50;
    const nearBottom = distFromBottom < 50;

    let btn: 'up' | 'down' | null = null;
    if (scrollingUp && !nearTop) {
      btn = 'up';
    } else if (!scrollingUp && !nearBottom) {
      btn = 'down';
    }

    setScrollBtn(btn);

    if (scrollBtnTimerRef.current) clearTimeout(scrollBtnTimerRef.current);
    if (btn) {
      scrollBtnTimerRef.current = setTimeout(() => setScrollBtn(null), 5000);
    }
  }, []);

  const handleScrollBtn = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (scrollBtn === 'up') {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }
  };

  /**
   * Text selection quote handler — shows a small floating button when text is
   * selected within the messages area. Works on both desktop (mouse drag) and
   * mobile (long-press to select). On touch, `mouseup` is unreliable, so we
   * key off `selectionchange` (fires on both) and evaluate once the user lets
   * go (`pointerup` / `touchend`) — that way the button doesn't flicker
   * mid-drag and appears when the selection is final.
   */
  useEffect(() => {
    let evalTimer: ReturnType<typeof setTimeout> | null = null;
    let lastSelectionText = '';

    const showBtn = (top: number, left: number) => {
      const btn = quoteBtnRef.current;
      if (!btn) return;
      btn.style.top = `${top}px`;
      btn.style.insetInlineStart = `${left}px`;
      btn.style.display = 'flex';
    };

    const hideBtn = () => {
      const btn = quoteBtnRef.current;
      if (btn) btn.style.display = 'none';
    };

    const evaluateSelection = () => {
      const container = messagesContainerRef.current;
      if (!container) return;

      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || !selection.rangeCount) {
        hideBtn();
        return;
      }

      const text = selection.toString().trim();
      if (!text) { hideBtn(); return; }

      const range = selection.getRangeAt(0);
      if (!container.contains(range.commonAncestorContainer)) {
        hideBtn();
        return;
      }

      // Only allow quoting from inside an actual chat MESSAGE — not from
      // skill badges, mode-switch indicators, model-switch labels, system
      // pills, or any other prose that happens to render inside the messages
      // container. Both ends of the selection must resolve to the SAME
      // `.message-text` element, otherwise we hide the button. (Dragging a
      // span across two bubbles also bails — quoting two different messages
      // as one block doesn't represent a coherent thought to reply to.)
      const resolveEl = (n: Node | null): Element | null => {
        if (!n) return null;
        return n.nodeType === Node.TEXT_NODE ? n.parentElement : (n as Element);
      };
      const startMessage = resolveEl(range.startContainer)?.closest('.message-text');
      const endMessage = resolveEl(range.endContainer)?.closest('.message-text');
      if (!startMessage || startMessage !== endMessage) {
        hideBtn();
        return;
      }

      const rect = range.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // Position: above the selection when possible, below it near the top edge.
      // On mobile the native selection handles take ~28px below the line, so we
      // nudge the button up a touch further to avoid overlap.
      const isCoarse = window.matchMedia('(pointer: coarse)').matches;
      const offsetAbove = isCoarse ? 44 : 36;

      let top = rect.top - containerRect.top + container.scrollTop - offsetAbove;
      let left = rect.right - containerRect.left - 40;

      if (left < 8) left = 8;
      if (left > containerRect.width - 90) left = containerRect.width - 90;
      if (top < container.scrollTop) {
        top = rect.bottom - containerRect.top + container.scrollTop + 10;
      }

      pendingQuoteTextRef.current = text;
      lastSelectionText = text;
      showBtn(top, left);
    };

    // selectionchange fires constantly during a drag. Debounce so we only
    // reposition when the selection stabilises; the "final" evaluation is
    // triggered by pointerup/touchend which clears the debounce and runs now.
    const handleSelectionChange = () => {
      if (evalTimer) clearTimeout(evalTimer);
      evalTimer = setTimeout(() => {
        // Only act while the user isn't actively pressing — avoids mid-drag
        // flicker on desktop. The pointerup/touchend handlers force an eval
        // once the gesture ends.
        const selection = window.getSelection();
        if (!selection || selection.isCollapsed) {
          hideBtn();
          return;
        }
        // Only show once the text changes — prevents repositioning while the
        // user fine-tunes with the native handles.
        if (selection.toString().trim() === lastSelectionText) return;
        evaluateSelection();
      }, 120);
    };

    const handleGestureEnd = () => {
      if (evalTimer) { clearTimeout(evalTimer); evalTimer = null; }
      // Slight delay so iOS's selection callout settles before we measure.
      setTimeout(evaluateSelection, 30);
    };

    const handlePointerDown = (e: PointerEvent) => {
      if (quoteBtnRef.current?.contains(e.target as Node)) return;
      // Starting a new gesture outside the button — hide until we have a
      // stable selection again.
      hideBtn();
      lastSelectionText = '';
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    document.addEventListener('pointerup', handleGestureEnd);
    document.addEventListener('touchend', handleGestureEnd);
    document.addEventListener('pointerdown', handlePointerDown);

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      document.removeEventListener('pointerup', handleGestureEnd);
      document.removeEventListener('touchend', handleGestureEnd);
      document.removeEventListener('pointerdown', handlePointerDown);
      if (evalTimer) clearTimeout(evalTimer);
    };
  }, []);

  const handleQuoteSelection = () => {
    const text = pendingQuoteTextRef.current;
    if (text) {
      setQuotedText(text);
    }

    // Clear the selection and hide the button
    window.getSelection()?.removeAllRanges();
    if (quoteBtnRef.current) quoteBtnRef.current.style.display = 'none';
  };

  const handleScrollToQuote = (quotedText: string) => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Find the message element containing the quoted text
    const messageEls = container.querySelectorAll('.message-text');
    for (const el of messageEls) {
      if (el.textContent?.includes(quotedText)) {
        const messageGroup = el.closest('.message-group');
        if (messageGroup) {
          messageGroup.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Flash highlight
          messageGroup.classList.add('highlight-flash');
          setTimeout(() => messageGroup.classList.remove('highlight-flash'), 1500);
        }
        break;
      }
    }
  };

  /**
   * A handoff just happened somewhere and THIS panel is the origin thread —
   * append the outbound card the backend wrote, so the link forward shows up
   * immediately instead of only after a reload.
   *
   * Guarded against duplicates because the panel may also refetch the thread
   * (reload, remount) and pick the same row up from the server.
   */
  useEffect(() => {
    const handler = (event: CustomEvent<{ conversationId: string; message: Message }>) => {
      const { conversationId, message: marker } = event.detail || ({} as any);
      if (!marker || !conversationId) return;
      if (conversation?.conversation_id !== conversationId) return;
      setMessages(prev =>
        prev.some(m => m.message_id === marker.message_id) ? prev : [...prev, marker]
      );
    };
    window.addEventListener('handoffMarkerAdded', handler as unknown as EventListener);
    return () => window.removeEventListener('handoffMarkerAdded', handler as unknown as EventListener);
  }, [conversation?.conversation_id]);

  /**
   * A pending post-Stop reconcile is scoped to the conversation that was
   * stopped. Switching threads (or unmounting the panel) cancels it — its
   * verdict must never land on a transcript it was not read from.
   */
  useEffect(() => () => {
    stopReconcileSeqRef.current += 1;
    if (stopReconcileTimerRef.current !== null) {
      window.clearTimeout(stopReconcileTimerRef.current);
      stopReconcileTimerRef.current = null;
    }
  }, [conversation?.conversation_id]);

  /**
   * Scroll to (and flash) a specific message — how a teleport lands on the
   * twin handoff card rather than at the bottom of the thread.
   *
   * Polls for the node instead of scrolling once: the event can arrive while
   * the panel is still fetching its messages, and a single attempt would
   * silently no-op on exactly the slow-network case where landing in the right
   * place matters most. Gives up after ~3s rather than looping forever.
   */
  /**
   * Scroll to (and ring) a specific message — how a teleport lands on the twin
   * handoff card instead of at the bottom of the thread.
   *
   * Driven by PROPS rather than a window event on purpose. The event version
   * was unwinnable: on a teleport that navigates, the request routinely fired
   * while this panel was still fetching, so the listener either dropped it or
   * retried through a stale closure that could never see the loaded
   * conversation. As a prop the request simply persists until the message
   * exists, and re-runs naturally when messages arrive.
   *
   * `focusNonce` exists so jumping to the SAME message twice still re-triggers
   * — without it the second jump would be a no-op on unchanged props.
   */
  const landedFocusRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!focusMessageId) return;
    // A jump must land EXACTLY ONCE. `messages` is in the dependency list so a
    // request that arrives before its target has rendered gets retried when the
    // thread loads — but that also means this effect re-runs on every streamed
    // token. Without this guard, typing a reply in a thread containing a
    // handoff card yanked the view back to the card on each chunk.
    if (landedFocusRef.current === focusNonce) return;
    let cancelled = false;
    let attempts = 0;
    const tryScroll = () => {
      if (cancelled) return;
      const container = messagesContainerRef.current;
      const el = container?.querySelector(`[data-message-id="${CSS.escape(focusMessageId)}"]`);
      if (el) {
        landedFocusRef.current = focusNonce;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('highlight-flash');
        window.setTimeout(() => el.classList.remove('highlight-flash'), 1500);
        return;
      }
      // ~6s of retries covers a cold fetch on a slow connection; past that the
      // target genuinely isn't in this thread and we stop rather than spin.
      if (++attempts < 40) window.setTimeout(tryScroll, 150);
    };
    tryScroll();
    return () => { cancelled = true; };
  }, [focusMessageId, focusNonce, messages]);

  /**
   * Show notification helper
   */
  // showNotification imported at top of file

  // Full-page drag & drop handlers
  const handlePageDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    pageDragCounterRef.current++;
    // Only show drop overlay for file drags, not text selection drags
    const hasFiles = Array.from(e.dataTransfer.items).some(item => item.kind === 'file');
    if (hasFiles) {
      setPageDragging(true);
    }
  }, []);

  const handlePageDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    pageDragCounterRef.current--;
    if (pageDragCounterRef.current === 0) {
      setPageDragging(false);
    }
  }, []);

  const handlePageDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handlePageDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPageDragging(false);
    pageDragCounterRef.current = 0;
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setDroppedFiles(files);
    }
  }, []);

  // Unified side-panel derivations: which tabs are available, which one is
  // effectively showing (falls back when the preferred tab has no content),
  // and the dock width (active view's width; 44px rail when collapsed).
  const artifactsTabAvailable = agentOutputsEnabled && !!artifacts.activeId;
  const wikiTabAvailable = wikiFeatureEnabled && wikiGraph.open;
  const driveTabAvailable = driveEdits.length > 0;
  const sidePanelOpen = artifactsTabAvailable || wikiTabAvailable || driveTabAvailable;
  const effectiveSideView: 'artifacts' | 'wiki' | 'drive' =
    sideView === 'drive'
      ? (driveTabAvailable ? 'drive' : artifactsTabAvailable ? 'artifacts' : 'wiki')
      : sideView === 'wiki'
        ? (wikiTabAvailable ? 'wiki' : artifactsTabAvailable ? 'artifacts' : 'drive')
        : (artifactsTabAvailable ? 'artifacts' : wikiTabAvailable ? 'wiki' : 'drive');
  const sidePanelWidth = sideCollapsed ? 44 : sideWidth;

  return (
    <div
      className={`conversation-panel ${isSplitView ? 'split-compact' : ''} ${pageDragging ? 'page-dragging' : ''} ${sidePanelOpen ? 'has-side-panel' : ''}`}
      data-panel={panelId}
      /* When `bot` hasn't fetched yet, fall back to the caller's
         `initialAgentColor` hint (passed by MainPage on first send so
         the chat opens already-tinted with the chosen agent's color).
         Only fall back to 'blue' as a last resort, for entry paths
         where neither the bot nor the hint is known. */
      data-agent-color={
        bot
          ? resolveAgentTextureColor(bot.avatar_color, bot.chatbot_name || '')
          : (initialAgentColor
             || readCachedAgentColor(conversation?.chatbot_id || initialConversation?.chatbot_id || initialBotId)
             || 'blue')
      }
      data-tint-by-agent={tintByAgent ? 'true' : undefined}
      style={{
        ...(sidePanelOpen
          ? {
              ['--side-panel-width' as any]: `${sidePanelWidth}px`,
              ['--chat-header-h' as any]: `${chatHeaderH}px`,
            }
          : {}),
      } as React.CSSProperties}
      onDragEnter={handlePageDragEnter}
      onDragLeave={handlePageDragLeave}
      onDragOver={handlePageDragOver}
      onDrop={handlePageDrop}
    >
      {/* Full-page drop overlay */}
      {pageDragging && (
        <div className="page-drop-overlay">
          <div className="page-drop-content">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span>{t('common.dropFilesHere')}</span>
          </div>
        </div>
      )}

      {loading && !initialMessage ? (
        <div className="loading-state">
          <div className="loading-spinner" />
          <div className="loading-text">{t('common.loadingConversation')}</div>
        </div>
      ) : loadFailed ? (
        // Recoverable load failure: shown after one silent auto-retry has
        // already failed. The user sees a clear retry control instead of a
        // blank chat or an indefinite spinner. Bumping `loadRetryNonce`
        // forces the init effect to re-run cleanly.
        <div
          className="loading-state conversation-load-error"
          data-testid="conversation-load-error"
          style={{ textAlign: 'center', padding: 24 }}
        >
          <div style={{ marginBottom: 12, fontWeight: 500 }}>
            {t('chat.failedToLoad') || 'Failed to load conversation'}
          </div>
          <div style={{ marginBottom: 16, opacity: 0.75 }}>
            {t('chat.failedToLoadHint') || 'The connection may have hiccupped. Try again.'}
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            data-testid="conversation-load-retry"
            onClick={() => {
              setLoadFailed(false);
              setLoadRetryNonce((n) => n + 1);
            }}
          >
            {t('common.retry') || 'Retry'}
          </button>
        </div>
      ) : (
        <>
          {/* Chat Header */}
          <div className="simple-chat-header" ref={chatHeaderRef}>
            <div className="chat-header-title-container">
              <div className="chat-header-bot-info">
                {(() => {
                  // panelLabel wins ONLY in split view (desktop side-by-side).
                  // On mobile branches stack/swipe one-at-a-time, so each panel
                  // shows the agent name as if it were its own conversation —
                  // the branch identity comes from the header switcher pill.
                  const useLabel = isSplitView && !!panelLabel;
                  const displayName = useLabel
                    ? panelLabel
                    : (bot?.chatbot_name || conversation?.chatbot_name);
                  // We're waiting for bot data if we have a bot ID (from the
                  // conversation or props) but no name yet. Render nothing then,
                  // or the header flashes "General Chat" before the real name.
                  const botLoading = !displayName && (conversation?.chatbot_id || initialBotId);
                  if (botLoading) return null;

                  // The markup itself lives in ConversationHeaderTitle so the
                  // White-Label preview renders the real header, not a copy.
                  // Bot avatar intentionally omitted for a named agent — it is
                  // already shown next to each assistant message.
                  return (
                    <ConversationHeaderTitle
                      displayName={displayName}
                      isPanelLabel={useLabel}
                      onOpenInfo={!useLabel && !isSplitView && !!bot
                        ? () => setShowBotInfo(!showBotInfo)
                        : undefined}
                      infoTitle={t('chat.agentInfo')}
                      fallbackTitle={conversation?.conversation_title || t('chat.generalChat')}
                      branchIcon={!!headerExtra}
                    />
                  );
                })()}
              </div>
            </div>

            {/* Bot Info Card */}
            {showBotInfo && bot && (
              <div className="bot-info-card-overlay" onClick={() => setShowBotInfo(false)}>
                <div className="bot-info-card" onClick={(e) => e.stopPropagation()}>
                  <div className="bot-info-card-header">
                    <span className="bot-info-card-avatar" dir="auto">
                      {bot.chatbot_name.substring(0, 2).toUpperCase()}
                    </span>
                    <div className="bot-info-card-title">
                      <h3 dir="auto">{bot.chatbot_name}</h3>
                      <span className="bot-info-card-visibility">
                        {bot.chatbot_visibility === 'private' ? t('common.private') :
                         bot.chatbot_visibility === 'team' ? t('common.shared') : t('common.public')}
                      </span>
                    </div>
                    <button className="bot-info-card-close" onClick={() => setShowBotInfo(false)}>x</button>
                  </div>
                  {bot.chatbot_description && (
                    <p className="bot-info-card-description" dir="auto">{bot.chatbot_description}</p>
                  )}
                  <div className="bot-info-card-models">
                    <h4>{t('chat.availableModels')}</h4>
                    <div className="bot-info-card-table-wrapper">
                      <table className="bot-info-card-table">
                        <thead>
                          <tr>
                            <th>{t('bots.modelCapabilities.model')}</th>
                            <th className="center-col">{t('bots.modelCapabilities.vision')}</th>
                            <th>{t('bots.modelCapabilities.webSearchTool')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredModels.map((model) => {
                            // Capabilities come straight from the family catalog
                            // (/ai/models/). Web-search tool names are provider-level
                            // product brand names, derived per provider.
                            const supportsVision = model.supports_vision ?? true;
                            const supportsWebSearch = model.supports_web_search ?? false;
                            const webSearchTool =
                              model.provider === 'openai' ? 'SearchGPT'
                              : model.provider === 'anthropic' ? 'Claude Web Search'
                              : model.provider === 'google' ? 'Google Search'
                              : '';
                            return (
                              <tr key={model.id}>
                                <td>
                                  <div className="model-name-cell">
                                    <span className="model-display-name" dir="auto">{model.name}</span>
                                    <span className="model-description" dir="auto">
                                      {model.description || model.resolved_model_name || ''}
                                    </span>
                                  </div>
                                </td>
                                <td className="center-col">
                                  {supportsVision ? (
                                    <span className="capability-icon supported" title={t('bots.modelCapabilities.supported')}>
                                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
                                      </svg>
                                    </span>
                                  ) : (
                                    <span className="capability-icon not-supported" title={t('bots.modelCapabilities.notSupported')}>
                                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
                                      </svg>
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {supportsWebSearch && webSearchTool ? (
                                    <span className={`web-search-badge ${webSearchTool === 'SearchGPT' ? 'searchgpt' : webSearchTool === 'Claude Web Search' ? 'claude' : 'google'}`}>
                                      {webSearchTool}
                                    </span>
                                  ) : (
                                    <span className="web-search-badge disabled">-</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Mode Selector — inline on the trailing edge of the header.
                Gated by `agents.behavior_switching` (resolved as
                `modeSliderEnabled` above) so a tenant without that flag
                doesn't see the slider even if the bot has a mode config.
                Staging removed this gate; we restore it because the
                feature flag is the architecture's source of truth. */}
            {modeSliderEnabled && conversation && (bot as any)?.mode_slider_config?.enabled && (
              <ModeSelector
                config={(bot as any).mode_slider_config}
                conversationId={conversation.conversation_id}
                activeIndex={activeModeIndex}
                onModeChange={setActiveModeIndex}
              />
            )}

            {/* Tool-invocation debug panel toggle — only when the feature
                flag is on AND a conversation exists. Sits just before any
                split-view close button so the X stays the rightmost control. */}
            {toolInvocationsEnabled && conversation?.conversation_id && (
              <button
                type="button"
                className="chat-header-debug-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setDebugPanelOpen((open) => !open);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                title={t('chat.debug.toggle') || 'Dev tools'}
                aria-label={t('chat.debug.toggle') || 'Dev tools'}
                data-testid="chat-debug-toggle"
                aria-pressed={debugPanelOpen}
                style={{
                  marginInlineStart: 8,
                  background: debugPanelOpen ? 'var(--primary, #3b82f6)' : 'transparent',
                  color: debugPanelOpen ? 'white' : 'currentColor',
                  border: '1px solid var(--claude-border, #d1d5db)',
                  borderRadius: 6,
                  width: 28,
                  height: 28,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                }}
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="16 18 22 12 16 6"/>
                  <polyline points="8 6 2 12 8 18"/>
                </svg>
                {debugEvents.length > 0 && (
                  <span
                    style={{
                      position: 'absolute',
                      top: -4,
                      right: -4,
                      minWidth: 14,
                      height: 14,
                      padding: '0 3px',
                      borderRadius: 7,
                      background: 'var(--primary, #3b82f6)',
                      color: 'white',
                      fontSize: 9,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                    }}
                  >
                    {debugEvents.length > 99 ? '99+' : debugEvents.length}
                  </span>
                )}
              </button>
            )}

            {/* Close panel button — split view only. Always the LAST element
                in the header so it sits flush against the panel's trailing
                edge (right in LTR, left in RTL via logical margin). Past the
                mode slider so the X is unambiguously the rightmost control.
                stopPropagation prevents the parent slide's refocus-on-click
                handler from racing the close. */}
            {isSplitView && onClose && (
              <button
                type="button"
                className="split-panel-close-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                onMouseDown={(e) => e.stopPropagation()}
                title={t('chat.closeBranch')}
                aria-label={t('chat.closeBranch')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}

          </div>

          {headerExtra}

          {/* Shared-project thread: name the project and show who has written
              here. Only rendered once the conversation exists — before the
              first send there are no contributors to show. */}
          {liveTurn && (
            <div className="thread-live" role="status" aria-live="polite">
              <span className="thread-live-dots" aria-hidden="true">
                <i /><i /><i />
              </span>
              {(t('projects.thread.answering') || 'The agent is answering {{name}}…')
                .replace('{{name}}', liveTurn)}
            </div>
          )}

          {(conversation?.project_id || projectId) && (
            <ProjectThreadStrip
              // `projectId` covers the window BEFORE the first send, when no
              // conversation row exists yet — without it the top of a brand-new
              // project thread was bare.
              projectId={(conversation?.project_id || projectId) as string}
              messages={messages}
              threadMode={conversation?.thread_mode || undefined}
              isOwnThread={
                !!signedInUser?.user_id && conversation?.user_id === signedInUser.user_id
              }
            />
          )}

          {/* Conversation token-budget banner (Change C) — soft warn or hard
              full. Sits above the message list so it's always visible. */}
          {conversationBanner && (
            <Alert
              type={conversationBanner.kind === 'full' ? 'error' : 'warning'}
              message={conversationBanner.message}
              showIcon
              closable={conversationBanner.kind === 'warning'}
              onClose={() => setConversationBanner(null)}
              style={{
                marginInline: 16,
                marginBlock: 8,
                borderRadius: 8,
              }}
              action={
                conversationBanner.kind === 'full' ? (
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent('corpbrain:newChat'));
                    }}
                  >
                    {t('chat.banners.CONVERSATION_FULL_NEW_CHAT')}
                  </Button>
                ) : undefined
              }
            />
          )}

          {/* Messages Area */}
          <div className="chat-messages-container" ref={messagesContainerRef} onScroll={handleMessagesScroll}>
            <MessageList
              messages={messages}
              loading={loading && !initialMessage}
              resolveModelName={resolveModelDisplayName}
              resolveModelIdentity={resolveModelIdentity}
              onEditMessage={handleEditMessage}
              onBranch={onBranchCreated ? handleBranch : undefined}
              onBranchToAgent={handleBranchToAgent}
              // Not gated on onBranchCreated: a handoff card can exist in a
              // thread opened anywhere, and it must always be able to navigate.
              onHandoffTeleport={handleHandoffTeleport}
              onHandoffSplit={handleHandoffSplit}
              onScrollToQuote={handleScrollToQuote}
              onContinue={isReadOnlyThread ? undefined : handleContinue}
              onArtifactOpen={agentOutputsEnabled
                ? (id: string) => { artifacts.openArtifact(id); setSideView('artifacts'); setSideCollapsed(false); }
                : undefined}
              activeSkills={activeSkills}
              streaming={isStreaming}
              branchPointMessageId={branchPointMessageId}
              thinkingSteps={thinkingSteps}
              thinkingPhase={thinkingPhase}
              thinkingFilename={thinkingFilename}
              thinkingText={liveStatusText}
              thinkingSince={liveStatus?.since}
              revealThinking={bot?.reveal_thinking}
              showSkillLabels={bot?.show_skill_labels}
              conversationId={conversation?.conversation_id}
              conversationTitle={conversation?.conversation_title}
              availableSkills={botSkills}
              skillFeedbackMap={skillFeedbackMap}
              onSkillFeedbackChange={(msgId: string, skills: Array<{ skill_id: string; skill_name: string }>) => setSkillFeedbackMap(prev => ({ ...prev, [msgId]: { ...prev[msgId], expected_skills: skills } }))}
              onRatingChange={(msgId: string, feedback: MessageFeedback) => setSkillFeedbackMap(prev => ({ ...prev, [msgId]: { ...prev[msgId], expected_skills: prev[msgId]?.expected_skills || [], ...feedback } }))}
              botColor={(() => {
                // Normalize to a TextureColor literal so MessageItem's
                // getAgentCoin() lookup succeeds — passing a raw hex
                // here would return undefined → broken assistant icon.
                const raw = bot?.avatar_color ?? (conversation as any)?.avatar_color ?? null;
                const name = bot?.chatbot_name ?? (conversation as any)?.chatbot_name ?? '';
                return raw || name ? resolveAgentTextureColor(raw, name) : null;
              })()}
              // A project thread is co-authored, so each user turn is labelled
              // with who wrote it. Off everywhere else — outside a project
              // every user message is by the same person.
              showSenderIdentity={!!(conversation?.project_id || projectId)}
              // ONLY for a thread this user is starting right now.
              //
              // Keyed off the `?project=` prop and nothing else. Two wrong
              // signals were tried first: `conversation.project_id` is set for
              // EVERY project thread, so opening someone else's conversation
              // greeted you with the new-conversation panel instead of their
              // history; and "no conversation yet" is never true here, because
              // this panel creates the row the moment an agent is picked, not
              // on first send. The prop is present only when the user came
              // through the project's New conversation button, which is
              // exactly the question being asked.
              projectId={projectId || null}
              botName={bot?.chatbot_name || conversation?.chatbot_name || undefined}
              botVariant={bot?.avatar_variant ?? (conversation as any)?.avatar_variant ?? null}
              branchDisabled={branchDisabled}
              branchDisabledReason={branchDisabledReason}
            />
            <div ref={messagesEndRef} />

            {/* Floating quote selection button — always in DOM, shown/hidden via direct style.
                pointerdown fires before the native selection-callout tap and for both mouse
                and touch; preventDefault keeps the selection alive long enough to capture it. */}
            <button
              ref={quoteBtnRef}
              className="quote-selection-btn"
              style={{ display: 'none' }}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleQuoteSelection();
              }}
              type="button"
            >
              <span className="quote-btn-icon">&ldquo;</span>
              <span className="quote-btn-label">{t('common.reply')}</span>
            </button>
          </div>
          <button
            className={`scroll-nav-btn ${scrollBtn ? 'visible' : ''}`}
            onClick={handleScrollBtn}
            title={scrollBtn === 'up' ? t('chat.scrollToTop') : t('chat.scrollToBottom')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ transform: scrollBtn === 'down' ? 'rotate(180deg)' : undefined, transition: 'transform 0.2s ease' }}
            >
              <polyline points="17 11 12 6 7 11" />
              <line x1="12" y1="6" x2="12" y2="18" />
              <line x1="5" y1="3" x2="19" y2="3" />
            </svg>
          </button>

          {/* A Drive tool in this turn waits for more Google access — the turn
              carries on by itself once the person allows it. */}
          {turnAccess.pending && (
            <div className="chat-google-access">
              <TurnGoogleAccessCard access={turnAccess.pending} onDismiss={turnAccess.dismiss} />
            </div>
          )}
          {turnConnector.pending && (
            <div className="chat-google-access">
              <TurnConnectorAccessCard ask={turnConnector.pending} onDismiss={turnConnector.dismiss} />
            </div>
          )}

          {/* Quote Reply Banner — above the input bar */}
          {quotedText && (
            <div className="quote-banner">
              <div className="quote-banner-content">
                <span className="quote-banner-label">{t('chat.quoteReply')}</span>
                <span className="quote-banner-text bidi-clamp" dir="auto">{quotedText}</span>
              </div>
              <button
                className="quote-banner-dismiss"
                onClick={() => setQuotedText(null)}
                title={t('chat.clearQuote')}
                type="button"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Input Area */}
          <div className={`chat-input-container ${isStreaming ? 'streaming' : ''}`}>
            {noModelsAvailable ? (
              <div
                className="no-models-available-panel"
                data-testid="no-available-models-panel"
                style={{
                  padding: 24,
                  textAlign: 'center',
                  border: '1px solid var(--claude-border, #d1d5db)',
                  borderRadius: 8,
                  background: 'var(--claude-bg-subtle, #fffbeb)',
                  color: 'var(--claude-text, #374151)',
                }}
              >
                <p style={{ margin: '0 0 12px', fontWeight: 500 }}>
                  {t('chat.noAvailableModels') ||
                    t('chat.noModelsAvailable')}
                </p>
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-testid="no-available-models-refresh"
                  onClick={async () => {
                    if (!conversation?.conversation_id) return;
                    try {
                      const resp = await api.get(`/conversations/${conversation.conversation_id}`);
                      if (!resp.data?.no_available_models) {
                        setNoModelsAvailable(false);
                        if (resp.data?.effective_model) {
                          userPickedRef.current = false;
                          setConversationModel(resp.data.effective_model);
                          setSelectedModel(toFamilyKey(resp.data.effective_model));
                        }
                        await refreshAvailableModels();
                      }
                    } catch (e) {
                      logger.debug('Refresh failed:', e);
                    }
                  }}
                >
                  {t('common.refresh') || 'Refresh'}
                </button>
              </div>
            ) : isReadOnlyThread ? (
              <div className="thread-readonly" role="note">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
                     strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="8" r="3.4" />
                  <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
                </svg>
                <span>
                  {t('projects.thread.readOnly')
                    || t('chat.someoneElsesThread')}
                </span>
              </div>
            ) : (
            <ChatInput
              onSendMessage={handleSendMessage}
              textareaDisabled={false}
              sendDisabled={sending || isStreaming || (loading && !!initialMessage)}
              selectedModel={selectedModel}
              onModelChange={handleModelChange}
              // Context meter — how full the conversation is against the
              // model's window; "Compact now" in its popover. A composer
              // chip at the trailing edge, absent under 50%.
              contextMeter={conversation ? (
                <ContextMeter
                  usage={contextUsage}
                  compacting={compacting}
                  onCompactNow={isReadOnlyThread ? undefined : handleCompactNow}
                />
              ) : undefined}
              connectors={connectorsAgentId ? (
                <AgentConnectors chatbotId={String(connectorsAgentId)} driveAllowed={!!driveSwitchAgentId}
                                 conversationId={conversation?.conversation_id || undefined} />
              ) : undefined}
              // THE chat. The tour's "you asked something", "you attached a
              // file" and "you switched the model" steps mean this box and the
              // Concierge's; the builder, the simulator and the Guide stay
              // silent (see ChatInput's `journeyDetection`).
              journeyDetection
              // No picker while the panel is still loading its agent: until
              // the agent is known the composer has only the tenant's list,
              // and a single-model agent's picker would show and then vanish.
              disableModelPicker={agentPending
                || bot?.conversation_capabilities?.supports_model_switching === false}
              disableAttachments={bot?.conversation_capabilities?.supports_file_attachments === false}
              allowedModels={bot?.handler_config?.allowed_models || bot?.conversation_capabilities?.allowed_models || 'all'}
              modelCatalogContext={conversation?.conversation_id ? { conversationId: conversation.conversation_id } : undefined}
              initialValue={editInputValue || initialMessage}
              forceValueUpdate={editTimestamp}
              isStreaming={isStreaming}
              onStopGeneration={handleStopGeneration}
              isEditing={!!editingMessage}
              onCancelEdit={handleCancelEdit}
              droppedFiles={droppedFiles}
              onDropProcessed={() => setDroppedFiles(null)}
              quotedText={quotedText || undefined}
              onClearQuote={() => setQuotedText(null)}
              onRestoreQuote={setQuotedText}
              // Mobile mode picker — chip rendered inside the input toolbar.
              // Desktop ignores these props (the chip is CSS display:none > 768px)
              // and continues to use the inline slider in the header.
              artifactSettings={
                agentOutputsEnabled && brandedAvailable && !!conversation?.conversation_id
                  && artifactFormats.length > 0
                  ? {
                      formats: artifactFormats as Array<'pptx' | 'docx'>,
                      branding: artifactBranding,
                      onToggle: handleArtifactBrandingToggle,
                    }
                  : undefined
              }
              modeConfig={(bot as any)?.mode_slider_config}
              conversationIdForMode={conversation?.conversation_id}
              activeModeIndex={activeModeIndex}
              onModeChange={setActiveModeIndex}
            />
            )}

          </div>

        </>
      )}

      {/* Dev-tools panel — gated on the observability.tool_invocations flag.
          Renders nothing when the flag is off or the user hasn't opened it.
          conversationId + tenantId power the new "Tokens" tab which polls
          /insights/conversations/<id>/{summary,timeline} for live cost data. */}
      {toolInvocationsEnabled && (
        <AgentDebugPanel
          open={debugPanelOpen}
          onClose={() => setDebugPanelOpen(false)}
          events={debugEvents}
          botSkills={botSkills}
          activeSkillEntries={activeSkillEntries}
          conversationId={conversation?.conversation_id ?? null}
          tenantId={conversation?.tenant_id ? String(conversation.tenant_id) : undefined}
        />
      )}

      {/* ── Unified right side panel ─────────────────────────────────────
          ONE dock hosts both Artifacts and the Wiki graph, switched by the
          single-line tab strip below (never two stacked panels). The active
          tab auto-jumps to whatever the agent is producing right now. */}
      {sidePanelOpen && (() => {
        const railBadge = artifacts.order.length + (wikiTabAvailable ? wikiGraph.notes.length : 0) + driveEdits.filter((e) => !e.reverted).length;
        const sideTabs = [
          artifactsTabAvailable && {
            key: 'artifacts' as const,
            label: t('chat.artifacts.panelTitle'),
            icon: (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
            ),
          },
          wikiTabAvailable && {
            key: 'wiki' as const,
            label: t('wiki.graph.title'),
            icon: <WikiWIcon size={13} />,
          },
          driveTabAvailable && {
            key: 'drive' as const,
            label: t('chat.drive.panelTitle'),
            icon: <GoogleDriveGlyph size={13} />,
          },
        ].filter(Boolean) as { key: 'artifacts' | 'wiki' | 'drive'; label: string; icon: React.ReactNode }[];
        return (
        <div className="side-panel-dock">
          {sideCollapsed ? (
            <aside className="side-panel--collapsed">
              <button
                type="button"
                className="side-panel__rail-btn"
                onClick={() => setSideCollapsed(false)}
                aria-label={t('chat.artifacts.expand')}
                title={t('chat.artifacts.expand')}
              >
                <svg className="rtl-flip" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 6l-6 6 6 6" />
                </svg>
                {railBadge > 0 && (
                  <span className="side-panel__rail-badge">{railBadge}</span>
                )}
              </button>
            </aside>
          ) : (
            <>
              {/* Single-line strip: view tabs + contextual controls + collapse */}
              <div className="side-panel__tabs">
                {sideTabs.map(tab => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`side-panel__tab ${effectiveSideView === tab.key ? 'is-active' : ''}`}
                    onClick={() => setSideView(tab.key)}
                    aria-pressed={effectiveSideView === tab.key}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
                <span className="side-panel__tabs-spacer" />
                {/* Trail/Full-map switching lives on the wiki canvas itself —
                    the floating segmented control in WikiGraphPanel. */}
                <button
                  type="button"
                  className="side-panel__icon-btn"
                  onClick={() => setSideCollapsed(true)}
                  aria-label={t('chat.artifacts.collapse')}
                  title={t('chat.artifacts.collapse')}
                >
                  <svg className="rtl-flip" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </button>
              </div>
              <div className="side-panel__body">
                {effectiveSideView === 'drive' ? (
                  <DriveFilePanel
                    edits={driveEdits}
                    activeFileId={driveActiveFileId}
                    onSelectFile={setDriveActiveFileId}
                    onRevert={handleDriveRevert}
                    onResize={setSideWidth}
                  />
                ) : effectiveSideView === 'artifacts' ? (
                  <ArtifactPanel
                    artifacts={artifacts.artifacts}
                    order={artifacts.order}
                    activeId={artifacts.activeId}
                    onSelect={artifacts.openArtifact}
                    onViewVersion={artifacts.viewVersion}
                    onResize={setSideWidth}
                    onDeleteVersion={artifacts.deleteVersion}
                  />
                ) : (
                  <WikiGraphPanel
                    notes={wikiGraph.notes}
                    sequence={wikiGraph.sequence}
                    currentId={wikiGraph.currentId}
                    mode={wikiGraph.mode}
                    fullNodes={wikiGraph.fullNodes}
                    fullEdges={wikiGraph.fullEdges}
                    fullLoading={wikiGraph.fullLoading}
                    conversationId={conversation?.conversation_id}
                    onResize={setSideWidth}
                    onSetMode={wikiGraph.setMode}
                  />
                )}
              </div>
            </>
          )}
        </div>
        );
      })()}

      {/* Agent chooser for "continue with another agent". Portals to body, so
          it escapes this panel's clipping and covers the whole viewport even
          when the panel is a narrow third of a split rail. */}
      <AgentPickerModal
        isOpen={!!agentPickerMessage}
        onClose={() => setAgentPickerMessage(null)}
        onSelect={handleAgentPicked}
        splitDisabled={branchDisabled}
        splitDisabledReason={branchDisabledReason}
        currentChatbotId={conversation?.chatbot_id || bot?.chatbot_id || null}
        currentChatbotName={conversation?.chatbot_name || bot?.chatbot_name || null}
      />
    </div>
  );
}
